// src/components/AdminDashboard.tsx
import React, { useState, useEffect, useCallback, useMemo, useRef, memo, FormEvent } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  collection, 
  getDocs, 
  query, 
  doc, 
  getDoc, 
  updateDoc, 
  deleteDoc,
  Timestamp,
  setDoc,
  onSnapshot,
  where,
  orderBy,
  limit as firestoreLimit
} from 'firebase/firestore';
import { db } from '../firebase';
import { UserData } from '../types/firebase';
import { TradingAccount, SubscriberInfo, BrokerType } from '../types/trading';
import { decrypt } from '../utils/encryption';
import { updateAccountStatus } from '../utils/firebaseHelpers';
import { useState as useLocalState } from 'react';

// Extend TradingAccount interface for admin view
interface AdminTradingAccount extends Omit<TradingAccount, 'strategies'> {
  password?: string;
  tradeVolume?: number;
  strategies?: Array<{
    id: string;
    name: string;
    tradeVolume: number;
    subscriptionId: string;
    isActive: boolean;
  }>;
}

// Add cache interface
interface UserCache {
  data: UserWithAccount[];
  timestamp: number;
  lastUserId: string | null;
  lastUpdated?: string;
}

interface UserWithAccount {
  id: string;
  userData: UserData;
  tradingAccounts: AdminTradingAccount[];
  hasActiveStrategy: boolean;
}

const planOptions = [
  { id: 'mobile', name: 'Mobile' },
  { id: 'alpha', name: 'Alpha' }
];

// Enum for verification status
enum VerificationStatus {
  IDLE = 'idle',
  CHECKING = 'checking',
  VERIFIED = 'verified',
  FAILED = 'failed'
}

// For debugging - enable string representation of enum
const VerificationStatusLabels = {
  [VerificationStatus.IDLE]: 'Idle',
  [VerificationStatus.CHECKING]: 'Checking',
  [VerificationStatus.VERIFIED]: 'Verified',
  [VerificationStatus.FAILED]: 'Failed'
}

// Add the getAccountKey function near the top of the file, after the state definitions
// Function to generate a unique key for each account
const getAccountKey = (userId: string, accountId: string) => `${userId}-${accountId}`;

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userData, logout } = useAuth();
  const [users, setUsers] = useState<UserWithAccount[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isAddingSubscription, setIsAddingSubscription] = useState<boolean>(false);
  const [isAddingAccount, setIsAddingAccount] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'noSubscription' | 'withSubscription' | 'activeTraders' | 'admins' | 'disconnected' | 'changes' | 'requests'>('noSubscription');
  const [newSubscription, setNewSubscription] = useState({
    planId: 'mobile',
    planName: 'Mobile',
    status: 'active',
    duration: 30, // days
    isTrial: false,
    trialDuration: 7, // days
  });
  const [isEditingSubscription, setIsEditingSubscription] = useState<boolean>(false);
  // Add broker filter state
  const [brokerFilter, setBrokerFilter] = useState<string>('all');
  
  // Add MetaAPI filter state
  const [metaApiFilter, setMetaApiFilter] = useState<string>('all'); // 'all', 'metaapi', 'non-metaapi'
  
  // Add sorting state
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  }>({
    key: 'subscription',
    direction: 'desc'
  });

  // Add state for account verification
  const [accountNumber, setAccountNumber] = useState<string>('');
  const [verificationStatus, setVerificationStatus] = useState<VerificationStatus>(VerificationStatus.IDLE);
  const [verificationMessage, setVerificationMessage] = useState<string>('');
  
  // State for MetaAPI connection
  const [metaAccountName, setMetaAccountName] = useState<string>('');
  const [metaPassword, setMetaPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [selectedServer, setSelectedServer] = useState<string>('');
  const [customServerName, setCustomServerName] = useState<string>('');
  const [useCustomServer, setUseCustomServer] = useState<boolean>(false);
  const [serverOptions, setServerOptions] = useState<Array<{name: string, description: string}>>([]);
  const [isLoadingServers, setIsLoadingServers] = useState<boolean>(false);
  const [serverSearchError, setServerSearchError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [accountSuccess, setAccountSuccess] = useState<string>('');
  const [accountError, setAccountError] = useState<string>('');
  const [selectedUserForAccount, setSelectedUserForAccount] = useState<UserWithAccount | null>(null);
  const [userCache, setUserCache] = useState<UserCache>({
    data: [],
    timestamp: 0,
    lastUserId: null
  });
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const isStaging = process.env.REACT_APP_ENVIRONMENT === 'staging';
  // Removed unused ref since realtime listener functionality was removed
  
  // State for password visibility
  const [visiblePasswords, setVisiblePasswords] = useState<{[key: string]: boolean}>({});
  
  // State for decrypted passwords
  const [decryptedPasswords, setDecryptedPasswords] = useState<{[key: string]: string}>({});
  
  // State for password decryption loading state
  const [decryptingPasswords, setDecryptingPasswords] = useState<{[key: string]: boolean}>({});

  // State to track what was recently copied
  const [copiedInfo, setCopiedInfo] = useState<{[key: string]: boolean}>({});
  
  // Add state for editing server names
  const [editingServers, setEditingServers] = useState<{[key: string]: string}>({});
  const [savingServers, setSavingServers] = useState<{[key: string]: boolean}>({});
  
  // Add state for editing account numbers
  const [editingAccountNumbers, setEditingAccountNumbers] = useState<{[key: string]: string}>({});
  const [savingAccountNumbers, setSavingAccountNumbers] = useState<{[key: string]: boolean}>({});
  
  // Add cache check function
  const isCacheValid = useCallback((cache: UserCache) => {
    const now = Date.now();
    return (
      cache.data.length > 0 &&
      now - cache.timestamp < CACHE_DURATION &&
      cache.lastUserId === currentUser?.uid
    );
  }, [currentUser]);

  // Check if user is authenticated and is an admin
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    if (!userData?.isAdmin) {
      navigate('/dashboard');
      return;
    }
  }, [currentUser, userData, navigate]);

  // Rewritten fetchUsers to use direct Firestore queries
  const fetchUsers = useCallback(async (forceRefresh: boolean = false) => {
    if (!currentUser || !userData?.isAdmin) {
      setIsLoading(false);
      return;
    }

    if (!forceRefresh && isCacheValid(userCache)) {
      setUsers(userCache.data);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const usersRef = collection(db, 'users');
      const usersQuery = query(usersRef, firestoreLimit(200));
      const usersSnapshot = await getDocs(usersQuery);

      const usersData = await Promise.all(
        usersSnapshot.docs.map(async (userDoc) => {
          const userId = userDoc.id;
          const userData = userDoc.data() as UserData;
          const tradingAccounts: AdminTradingAccount[] = [];
          let hasActiveStrategy = false;

          // Process each trading account
          const accountsSnapshot = await getDocs(collection(db, 'users', userId, 'tradingAccounts'));
          
          for (const accountDoc of accountsSnapshot.docs) {
            const accountData = accountDoc.data();
            const account: AdminTradingAccount = {
              id: accountDoc.id,
              userId: userId,
              accountNumber: accountData.accountNumber,
              accountName: accountData.accountName,
              server: accountData.server || accountData.serverName,
              serverName: accountData.server || accountData.serverName,
              isConnected: accountData.isConnected === true,
              platform: accountData.platform,
              brokerType: accountData.brokerType,
              createdAt: accountData.createdAt,
              updatedAt: accountData.updatedAt,
              password: accountData.password || '',
              metaApiAccountId: accountData.metaApiAccountId,
              strategies: []
            };

            // Fetch strategies for this account
            const subscriptionsSnapshot = await getDocs(
              query(
                collection(db, 'users', userId, 'tradingAccounts', accountDoc.id, 'subscriptions'),
                where('isActive', '==', true)
              )
            );

            // Process each subscription/strategy
            for (const subscriptionDoc of subscriptionsSnapshot.docs) {
              const subscriptionData = subscriptionDoc.data();
              const strategyId = subscriptionData.strategyId;

              if (strategyId) {
                const strategySnap = await getDoc(doc(db, 'strategies', strategyId));
                
                if (strategySnap.exists()) {
                  const strategyData = strategySnap.data();
                  if (!account.strategies) {
                    account.strategies = [];
                  }
                  account.strategies.push({
                    id: strategyId,
                    name: strategyData.name || 'Unknown Strategy',
                    tradeVolume: subscriptionData.tradeVolume || 0.01,
                    subscriptionId: subscriptionDoc.id,
                    isActive: true
                  });
                  hasActiveStrategy = true;
                }
              }
            }

            tradingAccounts.push(account);
          }

          const userWithAccount: UserWithAccount = {
            id: userId,
            userData: userData,
            tradingAccounts,
            hasActiveStrategy
          };

          return userWithAccount;
        })
      );

      setUserCache({
        data: usersData,
        timestamp: Date.now(),
        lastUserId: currentUser.uid
      });

      setUsers(usersData);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, userData, userCache, isCacheValid]);

  // Function to load more users
  const loadMoreUsers = async () => {
    if (!currentUser) return;
    
    try {
      setIsLoadingMore(true);
      
      // Get the timestamp of the last user in our list
      const lastUser = users[users.length - 1];
      if (!lastUser || !lastUser.userData.createdAt) {
        setIsLoadingMore(false);
        return;
      }
      
      // Use the createdAt timestamp for pagination
      const lastCreatedAt = lastUser.userData.createdAt;
      
      // Query for more users created after the last one
      const usersRef = collection(db, 'users');
      const usersQuery = query(
        usersRef,
        where('createdAt', '>', lastCreatedAt),
        firestoreLimit(50)
      );
      
      const usersSnapshot = await getDocs(usersQuery);
      
      if (usersSnapshot.empty) {
        setIsLoadingMore(false);
        return;
      }
      
      // Process each user
      const moreUsersData = await Promise.all(
        usersSnapshot.docs.map(async (userDoc) => {
          const userId = userDoc.id;
          const userData = userDoc.data() as UserData;
          const tradingAccounts: AdminTradingAccount[] = [];
          let hasActiveStrategy = false;

          // Fetch the user's trading accounts
          const accountsRef = collection(db, 'users', userId, 'tradingAccounts');
          const accountsSnapshot = await getDocs(accountsRef);

          // Process each trading account
          for (const accountDoc of accountsSnapshot.docs) {
            const accountData = accountDoc.data();
            const account: AdminTradingAccount = {
              id: accountDoc.id,
              userId: userId,
              accountNumber: accountData.accountNumber,
              accountName: accountData.accountName,
              server: accountData.server || accountData.serverName,
              serverName: accountData.server || accountData.serverName,
              isConnected: accountData.isConnected === true,
              platform: accountData.platform,
              brokerType: accountData.brokerType,
              createdAt: accountData.createdAt,
              updatedAt: accountData.updatedAt,
              password: accountData.password || '',
              metaApiAccountId: accountData.metaApiAccountId,
              strategies: []
            };

            // Fetch strategies for this account
            const subscriptionsRef = collection(db, 'users', userId, 'tradingAccounts', accountDoc.id, 'subscriptions');
            const subscriptionsQuery = query(subscriptionsRef, where('isActive', '==', true));
            const subscriptionsSnapshot = await getDocs(subscriptionsQuery);

            // Process each subscription/strategy
            for (const subscriptionDoc of subscriptionsSnapshot.docs) {
              const subscriptionData = subscriptionDoc.data();
              const strategyId = subscriptionData.strategyId;

              if (strategyId) {
                const strategyRef = doc(db, 'strategies', strategyId);
                const strategySnap = await getDoc(strategyRef);

                if (strategySnap.exists()) {
                  const strategyData = strategySnap.data();
                  if (!account.strategies) {
                    account.strategies = [];
                  }
                  account.strategies.push({
                    id: strategyId,
                    name: strategyData.name || 'Unknown Strategy',
                    tradeVolume: subscriptionData.tradeVolume || 0.01,
                    subscriptionId: subscriptionDoc.id,
                    isActive: true
                  });
                  hasActiveStrategy = true;
                }
              }
            }

            tradingAccounts.push(account);
          }

          return {
            id: userId,
            userData: userData,
            tradingAccounts,
            hasActiveStrategy
          } as UserWithAccount;
        })
      );
      
      // Merge the new users with existing ones, avoiding duplicates
      const newUsers = [...users];
      
      moreUsersData.forEach((newUser) => {
        // Check if user already exists
        const existingIndex = newUsers.findIndex(u => u.id === newUser.id);
        
        if (existingIndex >= 0) {
          // Update existing user data
          newUsers[existingIndex] = newUser;
        } else {
          // Add new user
          newUsers.push(newUser);
        }
      });
      
      // Update the users state and cache
      setUsers(newUsers);
      setUserCache({
        data: newUsers,
        timestamp: Date.now(),
        lastUserId: currentUser.uid
      });
      
    } catch (err) {
      console.error('Error loading more users:', err);
      setError('Failed to load more users. Please try again.');
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Run setup when component mounts or user changes - consolidated useEffect
  useEffect(() => {
    if (currentUser && userData?.isAdmin) {
      void fetchUsers();
    }
    
    // Make sure cleanup happens properly
    return () => {
      // Component unmounting
    };
  }, [currentUser, userData, fetchUsers]);

  // Add highlight function
  const highlightText = (text: string, query: string) => {
    if (!query) return text;
    
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return (
      <span>
        {parts.map((part, i) => 
          part.toLowerCase() === query.toLowerCase() ? 
            <mark key={i} className="bg-yellow-200">{part}</mark> : 
            part
        )}
      </span>
    );
  };

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const deleteUserAccount = async (userId: string, accountId: string) => {
    if (!window.confirm('Are you sure you want to delete this trading account? This will remove it from the dashboard and disconnect it from our services. The account will still exist with the broker.')) {
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Find the user and their trading account
      const user = users.find(u => u.id === userId);
      if (!user || !user.tradingAccounts) {
        throw new Error('Trading account not found');
      }
      
      // Delete the account document from Firestore
      await deleteDoc(doc(db, 'users', userId, 'tradingAccounts', accountId));
      
      // Update account status with account number and ID
      await updateAccountStatus(
        userId,
        'deleted_account',
        accountId,
        undefined,
        `Deleted trading account #${user.tradingAccounts.find(acc => acc.id === accountId)?.accountNumber} (ID: ${accountId})`
      );
      
      // Refresh the user list
      const updatedUsers = users.map(user => {
        if (user.id === userId) {
          return {
            ...user,
            tradingAccounts: user.tradingAccounts.filter(acc => acc.id !== accountId)
          };
        }
        return user;
      });
      
      setUsers(updatedUsers);
      alert('Trading account deleted successfully');
    } catch (err) {
      console.error('Error deleting account:', err);
      setError('Failed to delete the trading account.');
    } finally {
      setIsLoading(false);
    }
  };

  const removeFromStrategy = async (userId: string, accountId: string, subscriptionId: string) => {
    if (!window.confirm('Are you sure you want to remove this user from the strategy?')) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Get the user's trading account
      const user = users.find(u => u.id === userId);
      const account = user?.tradingAccounts.find(acc => acc.id === accountId);
      
      if (!user || !account) {
        throw new Error('Trading account not found');
      }

      // Remove from Firestore first
      const subscriptionRef = doc(db, 'users', userId, 'tradingAccounts', accountId, 'subscriptions', subscriptionId);
      await deleteDoc(subscriptionRef);

      // If account has MetaAPI ID, also call the API to unsubscribe
      if (account.metaApiAccountId) {
        try {
          const response = await fetch(
            `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/unsubscribeFromStrategy`,
            {
              method: 'DELETE',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                subscriberId: account.metaApiAccountId,
                subscriptionId: subscriptionId,
                mode: 'close-immediately'
              }),
            }
          );

          if (!response.ok) {
            const errorText = await response.text();
            console.warn('MetaAPI unsubscribe failed, but Firestore subscription was removed:', errorText);
          }
        } catch (apiError) {
          console.warn('MetaAPI unsubscribe failed, but Firestore subscription was removed:', apiError);
        }
      }

      // Update the UI
      const updatedUsers = users.map(user => {
        if (user.id === userId) {
          return {
            ...user,
            tradingAccounts: user.tradingAccounts.map(acc => {
              if (acc.id === accountId) {
                return {
                  ...acc,
                  strategies: acc.strategies?.filter(s => s.subscriptionId !== subscriptionId)
                };
              }
              return acc;
            }),
            hasActiveStrategy: user.tradingAccounts.some(acc => 
              acc.strategies?.some(s => s.subscriptionId !== subscriptionId)
            )
          };
        }
        return user;
      });

      setUsers(updatedUsers);
      alert('Strategy removed successfully');
    } catch (err) {
      console.error('Error removing strategy:', err);
      setError('Failed to remove strategy: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddSubscription = (userId: string) => {
    setSelectedUserId(userId);
    setIsAddingSubscription(true);
    setIsEditingSubscription(false);
    setNewSubscription({
      planId: 'mobile',
      planName: 'Mobile',
      status: 'active',
      duration: 30, // days
      isTrial: false,
      trialDuration: 7, // days
    });
  };
  
  const handleEditSubscription = (userId: string, subscription: UserData['subscription']) => {
    if (!subscription) return;
    
    setSelectedUserId(userId);
    setIsAddingSubscription(true);
    setIsEditingSubscription(true);
    
    // Calculate remaining duration in days
    const now = new Date();
    const expiresAt = subscription.expiresAt.toDate();
    const remainingDays = Math.max(1, Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
    
    // If trial is active, calculate remaining trial days
    let remainingTrialDays = 0;
    if (subscription.isTrial && subscription.trialEndsAt) {
      const trialEndsAt = subscription.trialEndsAt.toDate();
      if (trialEndsAt > now) {
        remainingTrialDays = Math.ceil((trialEndsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      }
    }
    
    setNewSubscription({
      planId: subscription.planId,
      planName: subscription.planName,
      status: subscription.status,
      duration: remainingDays,
      isTrial: subscription.isTrial || false,
      trialDuration: remainingTrialDays || (subscription.trialDuration || 0),
    });
  };

  const saveSubscription = async () => {
    if (!selectedUserId) return;
    
    try {
      setIsLoading(true);
      setSubscriptionError('');
      
      // Compute dates for the subscription
      const now = new Date();
      
      // Calculate expiration date from now + duration days
      const expirationDate = new Date(now.getTime() + newSubscription.duration * 24 * 60 * 60 * 1000);
      
      // If it's a trial, calculate trial end date
      let trialEndDate = null;
      if (newSubscription.isTrial) {
        trialEndDate = new Date(now.getTime() + newSubscription.trialDuration * 24 * 60 * 60 * 1000);
      }
      
      // Prepare the subscription object
      const subscriptionData = {
        planId: newSubscription.planId,
        planName: newSubscription.planName,
        status: newSubscription.status,
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
        expiresAt: Timestamp.fromDate(expirationDate),
        isTrial: newSubscription.isTrial,
      };
      
      // Add trial-specific fields if it's a trial
      if (newSubscription.isTrial && trialEndDate) {
        Object.assign(subscriptionData, {
          trialEndsAt: Timestamp.fromDate(trialEndDate),
          trialDuration: newSubscription.trialDuration,
        });
      }
      
      // Update the user document with the new subscription
      await updateDoc(doc(db, 'users', selectedUserId), {
        subscription: subscriptionData,
        updatedAt: Timestamp.fromDate(now)
      });
      
      // Fetch user data to update the UI
      void fetchUsers(true);
      
      // Reset state and close the modal
      setIsAddingSubscription(false);
      setSelectedUserId(null);
      setIsEditingSubscription(false);
      setNewSubscription({
        planId: 'mobile',
        planName: 'Mobile',
        status: 'active',
        duration: 30,
        isTrial: false,
        trialDuration: 7,
      });
      
      // Show success message
      alert(`Subscription ${isEditingSubscription ? 'updated' : 'added'} successfully`);
    } catch (err) {
      console.error(`Error ${isEditingSubscription ? 'updating' : 'adding'} subscription:`, err);
      setSubscriptionError(`Failed to ${isEditingSubscription ? 'update' : 'add'} subscription. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Function to cancel a subscription
  const cancelSubscription = async (userId: string) => {
    if (!window.confirm('Are you sure you want to cancel this subscription?')) {
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Get the user's current subscription
      const user = users.find(u => u.id === userId);
      if (!user?.userData.subscription) {
        throw new Error('User does not have an active subscription');
      }
      
      // Update only the status field to 'canceled'
      await updateDoc(doc(db, 'users', userId), {
        'subscription.status': 'canceled',
        'subscription.updatedAt': Timestamp.now()
      });
      
      // Refresh the user list
      void fetchUsers(true);
      
      // Show success message
      alert('Subscription canceled successfully');
    } catch (err) {
      console.error('Error canceling subscription:', err);
      setError('Failed to cancel subscription: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };
  
  // Function to reactivate a subscription
  const reactivateSubscription = async (userId: string) => {
    if (!window.confirm('Are you sure you want to reactivate this subscription?')) {
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Get the user's current subscription
      const user = users.find(u => u.id === userId);
      if (!user?.userData.subscription) {
        throw new Error('User does not have a subscription to reactivate');
      }
      
      // Update subscription status to active
      await updateDoc(doc(db, 'users', userId), {
        'subscription.status': 'active',
        'subscription.updatedAt': Timestamp.now()
      });
      
      // Refresh the user list
      void fetchUsers(true);
      
      // Show success message
      alert('Subscription reactivated successfully');
    } catch (err) {
      console.error('Error reactivating subscription:', err);
      setError('Failed to reactivate subscription: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle opening the add account modal
  const handleAddAccount = (user: UserWithAccount) => {
    setSelectedUserForAccount(user);
    setIsAddingAccount(true);
    
    // Pre-fill the account name with user's name
    setMetaAccountName(`${user.userData.firstName} ${user.userData.lastName}`);
    
    // Reset other form fields
    setAccountNumber('');
    setMetaPassword('');
    setSelectedServer('');
    setCustomServerName('');
    setUseCustomServer(false);
    setServerOptions([]);
    setIsLoadingServers(false);
    setServerSearchError('');
    setVerificationStatus(VerificationStatus.IDLE);
    setVerificationMessage('');
    setAccountError('');
    setAccountSuccess('');
  };
  
  // State for managing strategy subscriptions
  const [isManagingSubscription, setIsManagingSubscription] = useState<boolean>(false);
  const [selectedUserForSubscription, setSelectedUserForSubscription] = useState<UserWithAccount | null>(null);
  const [strategies, setStrategies] = useState<Array<{id: string, name: string, description: string, active: boolean}>>([]);
  const [isLoadingStrategies, setIsLoadingStrategies] = useState<boolean>(false);
  const [selectedStrategy, setSelectedStrategy] = useState<string>('');
  const [tradeVolume, setTradeVolume] = useState<number>(0.01);
  const [subscriptionSuccess, setSubscriptionSuccess] = useState<string>('');
  const [subscriptionError, setSubscriptionError] = useState<string>('');
  
  // Function to toggle admin status for a user
  const toggleAdminStatus = async (user: UserWithAccount) => {
    const newAdminStatus = !user.userData.isAdmin;
    const actionText = newAdminStatus ? 'grant' : 'revoke';
    
    if (!window.confirm(`Are you sure you want to ${actionText} admin privileges for ${user.userData.firstName} ${user.userData.lastName}?`)) {
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Update user document to change admin status
      const userRef = doc(db, 'users', user.id);
      await updateDoc(userRef, {
        isAdmin: newAdminStatus
      });
      
      // Update the users array with the new admin status
      const updatedUsers = users.map(u => {
        if (u.id === user.id) {
          return {
            ...u,
            userData: {
              ...u.userData,
              isAdmin: newAdminStatus
            }
          };
        }
        return u;
      });
      
      setUsers(updatedUsers);
      alert(`Admin privileges ${newAdminStatus ? 'granted' : 'revoked'} successfully`);
    } catch (err) {
      console.error(`Error ${actionText}ing admin privileges:`, err);
      setError(`Failed to ${actionText} admin privileges.`);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Function to toggle technical admin status for a user
  const toggleTechnicalAdminStatus = async (user: UserWithAccount) => {
    // Only admins can be technical admins
    if (!user.userData.isAdmin && !user.userData.isTechnicalAdmin) {
      alert('User must be an admin before they can be a technical admin.');
      return;
    }
    
    const newTechnicalAdminStatus = !user.userData.isTechnicalAdmin;
    const actionText = newTechnicalAdminStatus ? 'grant' : 'revoke';
    
    if (!window.confirm(`Are you sure you want to ${actionText} technical admin privileges for ${user.userData.firstName} ${user.userData.lastName}?`)) {
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Update user document to change technical admin status
      const userRef = doc(db, 'users', user.id);
      await updateDoc(userRef, {
        isTechnicalAdmin: newTechnicalAdminStatus
      });
      
      // Update the users array with the new technical admin status
      const updatedUsers = users.map(u => {
        if (u.id === user.id) {
          return {
            ...u,
            userData: {
              ...u.userData,
              isTechnicalAdmin: newTechnicalAdminStatus
            }
          };
        }
        return u;
      });
      
      setUsers(updatedUsers);
      alert(`Technical admin privileges ${newTechnicalAdminStatus ? 'granted' : 'revoked'} successfully`);
    } catch (err) {
      console.error(`Error ${actionText}ing technical admin privileges:`, err);
      setError(`Failed to ${actionText} technical admin privileges.`);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Function to search for MetaTrader servers
  const searchServers = async (customServer?: string) => {
    setIsLoadingServers(true);
    setAccountError('');
    
    try {
      // Set search term to specifically search for XM Global servers
      const searchTerm = customServer || "XM Global Limited";
      
      // Call the searchMetaTraderServers function
      const response = await fetch(
        `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/searchMetaTraderServers?term=${encodeURIComponent(searchTerm)}&platform=mt5`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch server list: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to retrieve server list');
      }
      
      // Format server options based on the response structure
      let formattedServers: Array<{name: string, description: string}> = [];
      
      if (data.servers && Array.isArray(data.servers)) {
        // Direct array of server objects - filter for XM Global servers
        formattedServers = data.servers
          .filter((server: any) => {
            const serverName = (server.name || '').toLowerCase();
            const serverDesc = (server.description || '').toLowerCase();
            return serverName.includes('xm') || serverName.includes('xmglobal') || 
                   serverDesc.includes('xm') || serverDesc.includes('xm global limited');
          })
          .map((server: any) => ({
            name: server.name || '',
            description: server.description || server.name || ''
          }));
      } else if (data.servers) {
        // Object with broker names as keys - filter for XM Global brokers
        Object.entries(data.servers).forEach(([brokerName, serverList]: [string, any]) => {
          const brokerLower = brokerName.toLowerCase();
          if (brokerLower.includes('xm') || brokerLower.includes('xm global limited')) {
            if (Array.isArray(serverList)) {
              serverList.forEach((serverName: string) => {
                formattedServers.push({
                  name: serverName,
                  description: `${serverName}`
                });
              });
            }
          }
        });
      }
      
      // If a custom server was provided and not found in results, add it to the list
      if (customServer && !formattedServers.some(server => server.name === customServer)) {
        formattedServers.unshift({
          name: customServer,
          description: `${customServer} (Custom)`
        });
        
        // Auto-select the custom server
        setSelectedServer(customServer);
      }
      
      setServerOptions(formattedServers);
      setVerificationStatus(VerificationStatus.VERIFIED);
      setVerificationMessage('Servers found! Please select a server.');
      
      if (formattedServers.length === 0) {
        setAccountError('No XM Global servers found. Try a different search term or add your server manually.');
      } else if (!selectedServer && formattedServers.length > 0) {
        // Pre-select the first server option if none is selected
        setSelectedServer(formattedServers[0].name);
      }
    } catch (error) {
      console.error('Error searching servers:', error);
      setAccountError('Failed to load server list. Please try again.');
      
      // If a custom server was provided, add it to the list anyway despite the error
      if (customServer) {
        setServerOptions([
          { name: customServer, description: `${customServer} (Custom)` },
          ...serverOptions // Keep existing options
        ]);
        setSelectedServer(customServer);
      } else {
        // Fallback to sample data for development/testing - use XM Global specific defaults
        const fallbackServers = [
          { name: 'XMGlobal-MT5', description: 'XM Global MT5 Server' },
          { name: 'XMGlobal-Demo', description: 'XM Global Demo Server' },
          { name: 'XMGlobal-Real', description: 'XM Global Real Server' },
        ];
        setServerOptions(fallbackServers);
        setSelectedServer(fallbackServers[0].name);
      }
    } finally {
      setIsLoadingServers(false);
    }
  };

  // Function to fetch available strategies
  const fetchStrategies = async () => {
    setIsLoadingStrategies(true);
    setSubscriptionError('');
    
    try {
      const response = await fetch(
        `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/getStrategies`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch strategies: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to retrieve strategies');
      }
      
      // Check if strategies are in data.strategies or data.data (adjust based on actual response)
      const strategiesArray = data.strategies || data.data || [];
      
      if (!Array.isArray(strategiesArray)) {
        console.error('Expected strategies array, but got:', strategiesArray);
        throw new Error('Invalid response format from server');
      }
      
      // Format and filter strategies - only show active ones
      const formattedStrategies = strategiesArray
        .filter((strategy: any) => strategy.active !== false)
        .map((strategy: any) => ({
          id: strategy._id,
          name: strategy.name,
          description: strategy.description || '',
          active: strategy.active !== false
        }));
      
      setStrategies(formattedStrategies);
    } catch (error) {
      console.error('Error fetching strategies:', error);
      setSubscriptionError('Failed to load strategies. Please try again later.');
    } finally {
      setIsLoadingStrategies(false);
    }
  };
  
  // Function to open the subscription management modal
  const handleManageSubscription = (user: UserWithAccount) => {
    if (!user.tradingAccounts) {
      alert('User must have a trading account before they can subscribe to strategies.');
      return;
    }
    
    setSelectedUserForSubscription(user);
    setIsManagingSubscription(true);
    setSelectedStrategy('');
    setTradeVolume(0.01);
    setSubscriptionSuccess('');
    setSubscriptionError('');
    
    // Fetch the strategies when opening the modal
    fetchStrategies();
  };
  
  // Function to get strategy details by ID
  const getStrategyDetails = (strategyId: string) => {
    const strategy = strategies.find(s => s.id === strategyId);
    return strategy ? strategy.name : 'Unknown Strategy';
  };
  
  const subscribeUserToStrategy = async () => {
    if (!selectedUserForSubscription || !selectedUserForSubscription.tradingAccounts) {
      setSubscriptionError('Missing required information. Please select a strategy.');
      return;
    }
    
    const strategyName = getStrategyDetails(selectedStrategy);
    
    setIsLoadingStrategies(true);
    setSubscriptionError('');
    setSubscriptionSuccess('');
    
    try {
      const accountId = selectedUserForSubscription.tradingAccounts[0].metaApiAccountId;
      const userId = selectedUserForSubscription.id;
      
      // Check if the user already exists as a subscriber
      const checkResponse = await fetch(
        `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/getSubscriber?accountId=${accountId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );
      
      let subscriberId = accountId; // Default to accountId
      
      // If the user doesn't exist as a subscriber yet, create them
      if (checkResponse.status === 404 || (checkResponse.ok && (await checkResponse.json()).subscriber === null)) {
        // Create subscriber first - use their MetaAPI account ID as the subscriber ID
        
        const createSubscriberResponse = await fetch(
          `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/createSubscriber`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              accountId: accountId,
              subscriberId: accountId, // Use account ID as subscriber ID
              name: selectedUserForSubscription.tradingAccounts[0].accountName
            }),
          }
        );
        
        if (!createSubscriberResponse.ok) {
          const errorText = await createSubscriberResponse.text();
          throw new Error(`Failed to create subscriber: ${createSubscriberResponse.status}. Details: ${errorText || 'No error details'}`);
        }
        
        const createSubscriberData = await createSubscriberResponse.json();
        
        if (!createSubscriberData.success) {
          throw new Error(createSubscriberData.message || 'Failed to create subscriber');
        }
        
        subscriberId = createSubscriberData.subscriberId || accountId;
      } else if (checkResponse.ok) {
        // If subscriber exists, get the ID
        const subscriberData = await checkResponse.json();
        if (subscriberData.success && subscriberData.subscriber) {
          subscriberId = subscriberData.subscriber._id;
        }
      }
      
      // Now subscribe to the strategy
      const response = await fetch(
        `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/subscribeToStrategy`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            subscriberId: subscriberId,
            strategyId: selectedStrategy,
            multiplier: 1.0, // Fixed multiplier
            tradeVolume: tradeVolume, // From user input
            copyStopLoss: true, // Always true
            copyTakeProfit: true, // Always true
            subscriberName: selectedUserForSubscription.tradingAccounts[0].accountName
          }),
        }
      );
      
      // Handle both successful JSON responses and 204 No Content responses
      if (response.ok || response.status === 204) {
        // Update the UI
        const updatedUsers = users.map(user => {
          if (user.id === userId) {
            return {
              ...user,
              tradingAccounts: user.tradingAccounts.map(acc => {
                if (acc.id === selectedUserForSubscription.tradingAccounts[0].id) {
                  return {
                    ...acc,
                    isConnected: true,
                    strategies: acc.strategies?.map(strategy => {
                      const targetStrategy = selectedUserForAccount?.tradingAccounts?.[0]?.strategies?.[0];
                      if (targetStrategy && strategy.subscriptionId === targetStrategy.subscriptionId) {
                        return {
                          ...strategy,
                          isActive: true
                        };
                      }
                      return strategy;
                    })
                  };
                }
                return acc;
              })
            };
          }
          return user;
        });
        
        setUsers(updatedUsers);
        setSubscriptionSuccess(`Successfully subscribed user to strategy: ${strategyName}`);
        
        // Close the modal after a delay
        setTimeout(() => {
          setIsManagingSubscription(false);
          setSelectedUserForSubscription(null);
        }, 2000);
      } else {
        const errorText = await response.text();
        throw new Error(`Failed to subscribe: ${response.status}. Details: ${errorText || 'No error details'}`);
      }
    } catch (error) {
      console.error('Error subscribing to strategy:', error);
      setSubscriptionError('Failed to subscribe to strategy. Please try again later.');
    } finally {
      setIsLoadingStrategies(false);
    }
  };
  
  // Function to check account affiliation using Firebase HTTP Function
  const checkAffiliation = async () => {
    if (!accountNumber.trim()) {
      setVerificationMessage('Please enter an account number');
      return;
    }
    
    setVerificationStatus(VerificationStatus.CHECKING);
    setVerificationMessage('Checking affiliation...');
    
    try {
      // Call the checkAffiliation function
      const response = await fetch(
        `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/checkAffiliation?accountNumber=${accountNumber}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      const responseData = await response.json();
      
      if (responseData.isAffiliated) {
        setVerificationStatus(VerificationStatus.VERIFIED);
        setVerificationMessage(`Account #${accountNumber} is affiliated with T1 FX Team! You can now connect it.`);
        
        // If verification successful, automatically search for servers
        searchServers();
      } else {
        setVerificationStatus(VerificationStatus.FAILED);
        setVerificationMessage(`Account #${accountNumber} is not affiliated. ${responseData.message || 'Please check the account number or contact support.'}`);
      }
    } catch (error) {
      console.error('Affiliation check error:', error);
      setVerificationStatus(VerificationStatus.FAILED);
      setVerificationMessage('Server error. Please try again later.');
    }
  };

  // Function to add trading account
  const addTradingAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!metaAccountName.trim() || !metaPassword.trim()) {
      setAccountError('Please fill in all required fields');
      return;
    }
    
    if (!selectedUserForAccount) {
      setAccountError('No user selected');
      return;
    }

    if (!accountNumber.trim()) {
      setAccountError('Please enter an account number');
      return;
    }
    
    if (!selectedServer && serverOptions.length === 0) {
      setAccountError('Please select a server or enter a custom server name');
      return;
    }
    
    setIsSubmitting(true);
    setAccountError('');
    setAccountSuccess('');
    
    try {
      // Skip affiliation check and server search if we already verified
      if (verificationStatus !== VerificationStatus.VERIFIED) {
        setAccountError('Please verify the account affiliation first by clicking the "Check" button');
        setIsSubmitting(false);
        return;
      }

        setAccountSuccess('Trading account added successfully!');
        
        // Store account information in Firestore
        const accountRef = doc(db, 'users', selectedUserForAccount.id, 'tradingAccounts', accountNumber);
        await setDoc(accountRef, {
          userId: selectedUserForAccount.id,
          accountNumber,
          accountName: metaAccountName,
          server: selectedServer || serverOptions[0]?.name,
          serverName: selectedServer || serverOptions[0]?.name,
          isConnected: true,
          platform: 'mt5',
          brokerType: BrokerType.XM,
          createdAt: new Date(),
          updatedAt: new Date()
        });
        
        const updatedUsers = users.map(user => {
          if (user.id === selectedUserForAccount.id) {
            return {
              ...user,
              tradingAccounts: user.tradingAccounts.map(acc => {
                if (acc.id === selectedUserForAccount.tradingAccounts[0].id) {
                  return {
                    ...acc,
                    isConnected: true,
                    strategies: acc.strategies?.map(strategy => {
                      const targetStrategy = selectedUserForAccount?.tradingAccounts?.[0]?.strategies?.[0];
                      if (targetStrategy && strategy.subscriptionId === targetStrategy.subscriptionId) {
                        return {
                          ...strategy,
                          isActive: true
                        };
                      }
                      return strategy;
                    })
                  };
                }
                return acc;
              })
            };
          }
          return user;
        });
        
      setUsers(updatedUsers as UserWithAccount[]);
        
        setTimeout(() => {
          setIsAddingAccount(false);
          setSelectedUserForAccount(null);
          setAccountNumber('');
          setMetaAccountName('');
          setMetaPassword('');
          setSelectedServer('');
          setVerificationStatus(VerificationStatus.IDLE);
        }, 2000);
    } catch (error) {
      console.error('Error adding trading account:', error);
      setAccountError('An unexpected error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add sorting function
  const sortUsers = (users: UserWithAccount[]) => {
    return [...users].sort((a, b) => {
      if (sortConfig.key === 'name') {
        const nameA = `${a.userData.firstName} ${a.userData.lastName}`.toLowerCase();
        const nameB = `${b.userData.firstName} ${b.userData.lastName}`.toLowerCase();
        return sortConfig.direction === 'asc' 
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      }
      
      if (sortConfig.key === 'subscription') {
        // First, handle users with no subscription
        if (!a.userData.subscription && b.userData.subscription) return -1;
        if (a.userData.subscription && !b.userData.subscription) return 1;
        
        // If both have subscriptions, sort by status
        if (a.userData.subscription && b.userData.subscription) {
          const statusOrder: Record<string, number> = { active: 0, expired: 1, canceled: 2 };
          const statusA = a.userData.subscription.status as keyof typeof statusOrder;
          const statusB = b.userData.subscription.status as keyof typeof statusOrder;
          return sortConfig.direction === 'asc'
            ? statusOrder[statusA] - statusOrder[statusB]
            : statusOrder[statusB] - statusOrder[statusA];
        }
        
        // If neither has subscription, sort by creation date
        return sortConfig.direction === 'asc'
          ? (a.userData.createdAt?.seconds || 0) - (b.userData.createdAt?.seconds || 0)
          : (b.userData.createdAt?.seconds || 0) - (a.userData.createdAt?.seconds || 0);
      }

      if (sortConfig.key === 'strategy') {
        // First, handle users with no strategy subscription
        if (!a.hasActiveStrategy && b.hasActiveStrategy) return -1;
        if (a.hasActiveStrategy && !b.hasActiveStrategy) return 1;
        
        // If both have strategy subscriptions, sort by strategy name
        if (a.hasActiveStrategy && b.hasActiveStrategy) {
          const strategyA = a.tradingAccounts?.[0]?.strategies?.[0];
          const strategyB = b.tradingAccounts?.[0]?.strategies?.[0];
          const nameA = strategyA?.name || '';
          const nameB = strategyB?.name || '';
          return sortConfig.direction === 'asc'
            ? nameA.localeCompare(nameB)
            : nameB.localeCompare(nameA);
        }
        
        // If neither has strategy subscription, sort by creation date
        return sortConfig.direction === 'asc'
          ? (a.userData.createdAt?.seconds || 0) - (b.userData.createdAt?.seconds || 0)
          : (b.userData.createdAt?.seconds || 0) - (a.userData.createdAt?.seconds || 0);
      }
      
      if (sortConfig.key === 'createdAt') {
        const dateA = a.userData.createdAt?.seconds || 0;
        const dateB = b.userData.createdAt?.seconds || 0;
        return sortConfig.direction === 'asc'
          ? dateA - dateB
          : dateB - dateA;
      }
      
      return 0;
    });
  };

  // Add sort handler
  const handleSort = (key: string) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Add a helper function to normalize broker type values for comparison
  const normalizeBrokerType = (brokerType: string | undefined): string => {
    if (!brokerType) return '';
    return brokerType.toUpperCase();
  };

  // Update the filteredUsers memo to include broker filtering
  const filteredUsers = useMemo(() => {
    // Remove debugging logs
    
    const filtered = users.filter(user => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = (
        (user.userData?.firstName || '').toLowerCase().includes(searchLower) ||
        (user.userData?.lastName || '').toLowerCase().includes(searchLower) ||
        (user.userData?.email || '').toLowerCase().includes(searchLower) ||
        (user.userData?.mobile && user.userData.mobile.toLowerCase().includes(searchLower))
      );
      
      // Add broker filtering with normalized comparison
      let matchesBroker = true;
      if (brokerFilter !== 'all' && user.tradingAccounts) {
        matchesBroker = normalizeBrokerType(user.tradingAccounts[0].brokerType) === normalizeBrokerType(brokerFilter);
      }
      
      // Add MetaAPI filtering
      let matchesMetaApi = true;
      if (metaApiFilter !== 'all' && user.tradingAccounts && user.tradingAccounts.length > 0) {
        const hasMetaApiAccount = user.tradingAccounts.some(account => account.metaApiAccountId);
        if (metaApiFilter === 'metaapi') {
          matchesMetaApi = hasMetaApiAccount;
        } else if (metaApiFilter === 'non-metaapi') {
          matchesMetaApi = !hasMetaApiAccount;
        }
      }
      
      return matchesSearch && matchesBroker && matchesMetaApi;
    });
    
    return sortUsers(filtered);
  }, [users, searchQuery, sortConfig, brokerFilter, metaApiFilter]);

  // Add cache invalidation on user data changes
  useEffect(() => {
    if (currentUser?.uid !== userCache.lastUserId) {
      setUserCache({
        data: [],
        timestamp: 0,
        lastUserId: null
      });
    }
  }, [currentUser, userCache.lastUserId]);

  // Filter users based on the active tab
  const tabFilteredUsers = useMemo(() => {
    // Debug disconnected admins
    const disconnectedAdmins = filteredUsers.filter(user => 
      user.userData.isAdmin && 
      user.userData.subscription?.status === 'active' && 
      user.tradingAccounts.some(acc => !acc.isConnected)
    );
    
    // Debug all users with trading accounts
    const usersWithAccounts = filteredUsers.filter(user => user.tradingAccounts);
    
    return filteredUsers.filter(user => {
      switch (activeTab) {
        case 'noSubscription':
          return !user.userData.isAdmin && (!user.userData.subscription || user.userData.subscription.status !== 'active');
        case 'withSubscription':
          return !user.userData.isAdmin && user.userData.subscription && user.userData.subscription.status === 'active' && !user.hasActiveStrategy;
        case 'activeTraders':
          return !user.userData.isAdmin && 
                 user.userData.subscription && 
                 user.userData.subscription.status === 'active' && 
                 user.tradingAccounts && 
                 user.hasActiveStrategy &&
                 user.tradingAccounts.every(acc => acc.isConnected); // All accounts must be connected
        case 'admins':
          return user.userData.isAdmin;
        case 'disconnected':
          return user.userData.subscription?.status === 'active' && 
                 user.tradingAccounts.some(acc => !acc.isConnected) && 
                 user.hasActiveStrategy;
        case 'changes':
          return !!user.userData.accountStatus?.action && user.userData.accountStatus.action !== 'change_confirmed';
        case 'requests':
          return user.userData.accountStatus?.action === 'subscription_requested';
        default:
          return true;
      }
    });
  }, [filteredUsers, activeTab]);

  // Update the navigation function to use a more effective approach
  const handleBackToDashboard = () => {
    // Clean up any listeners first
    // Removed references to realtimeUnsubscribeRef since realtime listener functionality was removed
    
    // Clear any state that might be causing issues
    setUsers([]);
    setUserCache({ data: [], timestamp: 0, lastUserId: null });
    setIsLoading(false);
    
    // Use direct navigation first, then fallback to window.location if needed
    try {
      navigate('/dashboard', { replace: true });
      
      // As a fallback, use location.href after a short delay
      setTimeout(() => {
        if (window.location.pathname !== '/dashboard') {
          window.location.href = '/dashboard';
        }
      }, 200);
    } catch (e) {
      console.error('Navigation error:', e);
      window.location.href = '/dashboard';
    }
  };

  // Currently not in use - deployment toggle functionality is disabled in the UI
  const toggleDeploymentStatus = async (userId: string, accountId: string, currentState: string) => {
    if (!currentUser || !window.confirm(`Are you sure you want to toggle the deployment status of this trading account?`)) {
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Get the trading account reference
      const accountRef = doc(db, 'users', userId, 'tradingAccounts', accountId);
      
      // Toggle the isConnected status
      const isConnected = currentState !== 'DEPLOYED'; // If not deployed, we'll set to connected
      
      // Update the account in Firestore
      await updateDoc(accountRef, {
        isConnected,
        updatedAt: new Date()
      });
      
      // Update local state
      setUsers(prevUsers => 
        prevUsers.map(user => {
          if (user.id === userId && user.tradingAccounts) {
            return {
              ...user,
              tradingAccounts: user.tradingAccounts.map(acc => {
                if (acc.id === accountId) {
                  return {
                    ...acc,
                    isConnected,
                    state: isConnected ? 'DEPLOYED' : 'UNDEPLOYED'
                  };
                }
                return acc;
              })
            };
          }
          return user;
        })
      );
      
      // Show success message
      alert(`Account successfully ${isConnected ? 'deployed' : 'undeployed'}`);
      
      // Force refresh data after a short delay to get updated state
      setTimeout(() => {
        fetchUsers(true);
      }, 5000);
      
    } catch (error) {
      console.error(`Error toggling deployment status:`, error);
      alert(`Failed to toggle account status. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  // Update cache invalidation on user data changes
  useEffect(() => {
    if (currentUser?.uid !== userCache.lastUserId) {
      setUserCache({
        data: [],
        timestamp: 0,
        lastUserId: null
      });
    }
  }, [currentUser, userCache.lastUserId]);

  // Calculate counts for each tab
  const tabCounts = useMemo(() => {
    // First filter users by broker type and search query
    const searchLower = searchQuery.toLowerCase();
    const filteredByBrokerAndSearch = users.filter(user => {
      const matchesSearch = (
        (user.userData?.firstName || '').toLowerCase().includes(searchLower) ||
        (user.userData?.lastName || '').toLowerCase().includes(searchLower) ||
        (user.userData?.email || '').toLowerCase().includes(searchLower) ||
        (user.userData?.mobile && user.userData.mobile.toLowerCase().includes(searchLower))
      );

      // Apply broker filter
      const matchesBroker = brokerFilter === 'all' || 
        (user.tradingAccounts && 
         normalizeBrokerType(user.tradingAccounts[0].brokerType) === normalizeBrokerType(brokerFilter));

      return matchesSearch && matchesBroker;
    });
      
    // Then separate admin users from regular users
    const regularUsers = filteredByBrokerAndSearch.filter(user => !user.userData.isAdmin);
    const adminUsers = filteredByBrokerAndSearch.filter(user => !!user.userData.isAdmin);
    
    // Count users with unconfirmed changes
    const usersWithChanges = filteredByBrokerAndSearch.filter(
      user => !!user.userData.accountStatus?.action && 
              user.userData.accountStatus.action !== 'change_confirmed'
    );
    
    // Count disconnected users (active subscription, has account, but not connected) - include admins
    const disconnectedUsers = filteredByBrokerAndSearch.filter(
      user => user.userData.subscription?.status === 'active' && 
              user.tradingAccounts.some(acc => !acc.isConnected) && 
              user.hasActiveStrategy
    );
    
    // Count users with pending subscription requests
    const usersWithRequests = filteredByBrokerAndSearch.filter(
      user => user.userData.accountStatus?.action === 'subscription_requested'
    );
    
    return {
      // For regular tabs, only count non-admin users who aren't disconnected
      noSubscription: regularUsers.filter(user => !user.userData.subscription || user.userData.subscription.status !== 'active').length,
      withSubscription: regularUsers.filter(user => 
        user.userData.subscription && 
        user.userData.subscription.status === 'active' && 
        (!user.tradingAccounts || !user.hasActiveStrategy)
      ).length,
      activeTraders: regularUsers.filter(user => 
        user.userData.subscription && 
        user.userData.subscription.status === 'active' && 
        user.tradingAccounts && 
        user.hasActiveStrategy &&
        user.tradingAccounts.every(acc => acc.isConnected) // All accounts must be connected
      ).length,
      // Admin count stays the same
      admins: adminUsers.length,
      // Add changes count
      changes: usersWithChanges.length,
      // Add disconnected count
      disconnected: disconnectedUsers.length,
      // Add requests count
      requests: usersWithRequests.length
    };
  }, [users, brokerFilter, searchQuery]); // Add searchQuery to dependencies

  // Function to manually toggle connection status (for admin testing purposes)
  const toggleConnectionStatus = async (userId: string, accountId: string) => {
    if (!window.confirm('Are you sure you want to toggle the connection status of this trading account?')) {
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Find the user and their trading account
      const user = users.find(u => u.id === userId);
      if (!user || !user.tradingAccounts) {
        throw new Error('Trading account not found');
      }
      
      // Get the current isConnected status
      const isCurrentlyConnected = user.tradingAccounts[0].isConnected;
      
      // Toggle the isConnected status
      const accountRef = doc(db, 'users', userId, 'tradingAccounts', accountId);
      await updateDoc(accountRef, {
        isConnected: !isCurrentlyConnected,
        updatedAt: new Date()
      });
      
      // Update local state
      const updatedUsers = users.map(user => {
        if (user.id === userId && user.tradingAccounts) {
          return {
            ...user,
            tradingAccounts: user.tradingAccounts.map(acc => {
              if (acc.id === accountId) {
                return {
                  ...acc,
                  isConnected: !isCurrentlyConnected
                };
              }
              return acc;
            })
          };
        }
        return user;
      });
      
      setUsers(updatedUsers);
      
      // Show success message
      alert(`Account is now ${!isCurrentlyConnected ? 'connected' : 'disconnected'}`);
      
      // Force refresh after a short delay to ensure the UI is updated with the latest state
      setTimeout(() => {
        fetchUsers(true);
      }, 1000);
      
    } catch (err) {
      console.error('Error toggling connection status:', err);
      setError('Failed to toggle connection status.');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to create a test disconnected user
  const createTestDisconnectedUser = async () => {
    if (!window.confirm('Are you sure you want to create a test disconnected user? This is for debugging purposes only.')) {
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Find an admin user that we can use as a test case
      const adminUser = users.find(u => u.userData.isAdmin && u.tradingAccounts);
      
      if (!adminUser || !adminUser.tradingAccounts) {
        alert('Could not find an admin user with a trading account to use as a test case.');
        return;
      }
      
      // Get the account reference
      const accountRef = doc(db, 'users', adminUser.id, 'tradingAccounts', adminUser.tradingAccounts[0].id);
      
      // Set isConnected to false
      await updateDoc(accountRef, {
        isConnected: false,
        updatedAt: new Date()
      });
      
      // Force refresh
      alert(`Created test disconnected user: ${adminUser.userData.firstName} ${adminUser.userData.lastName}`);
      
      // Refresh data
      setTimeout(() => {
        fetchUsers(true);
      }, 500);
      
    } catch (err) {
      console.error('Error creating test disconnected user:', err);
      alert('Failed to create test disconnected user. See console for details.');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to copy account information to clipboard
  const copyToClipboard = (text: string, accountKey: string) => {
    if (!text) return;
    
    navigator.clipboard.writeText(text)
      .then(() => {
        // Set copied state for this field
        setCopiedInfo(prev => ({
          ...prev,
          [accountKey]: true
        }));
        
        // Reset copied state after 2 seconds
        setTimeout(() => {
          setCopiedInfo(prev => ({
            ...prev,
            [accountKey]: false
          }));
        }, 2000);
      })
      .catch(err => {
        console.error('Failed to copy information: ', err);
      });
  };

  // Function to toggle password visibility
  const togglePasswordVisibility = (userId: string, accountId: string) => {
    const accountKey = getAccountKey(userId, accountId);
    const user = users.find(u => u.id === userId);
    const account = user?.tradingAccounts.find(acc => acc.id === accountId);
    const shouldShow = !visiblePasswords[accountKey];
    
    // Update visibility state
    setVisiblePasswords(prev => ({
      ...prev,
      [accountKey]: shouldShow
    }));
    
    // If showing password and we haven't decrypted it yet
    if (shouldShow && account?.password && !decryptedPasswords[accountKey]) {
      // Set decrypting state to true
      setDecryptingPasswords(prev => ({
        ...prev,
        [accountKey]: true
      }));
      
      try {
        // First check if the password is empty or not defined
        if (!account.password) {
          setDecryptedPasswords(prev => ({
            ...prev,
            [accountKey]: 'No password stored'
          }));
          setDecryptingPasswords(prev => ({
            ...prev,
            [accountKey]: false
          }));
          return;
        }
        
        // Try to decrypt the password
        const encryptedPassword = account.password;
        const decryptedPassword = decrypt(encryptedPassword);
        
        // If decryption returns empty string, show a message
        if (!decryptedPassword) {
          setDecryptedPasswords(prev => ({
            ...prev,
            [accountKey]: 'No password available'
          }));
          setDecryptingPasswords(prev => ({
            ...prev,
            [accountKey]: false
          }));
          return;
        }
        
        // Store the decrypted password
        setDecryptedPasswords(prev => ({
          ...prev,
          [accountKey]: decryptedPassword
        }));
        setDecryptingPasswords(prev => ({
          ...prev,
          [accountKey]: false
        }));
      } catch (error) {
        console.error('Error decrypting password:', error);
        // If decryption fails, store a user-friendly message
        setDecryptedPasswords(prev => ({
          ...prev,
          [accountKey]: 'Unable to decrypt password'
        }));
        setDecryptingPasswords(prev => ({
          ...prev,
          [accountKey]: false
        }));
      }
    }
  };

  // Function to confirm account status changes
  const confirmAccountChanges = async (userId: string) => {
    if (!window.confirm('Are you sure you want to confirm these changes? This will mark them as acknowledged.')) {
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Update the user document to set the account status action to 'change_confirmed'
      const userRef = doc(db, 'users', userId);
      
      // Keep all existing account status fields but update the action
      await updateDoc(userRef, {
        'accountStatus.action': 'change_confirmed',
        'accountStatus.lastUpdated': Timestamp.now()
      });
      
      // Update our local state to reflect the change
      setUsers(prevUsers => 
        prevUsers.map(user => {
          if (user.id === userId) {
            return {
              ...user,
              userData: {
                ...user.userData,
                accountStatus: {
                  ...user.userData.accountStatus!,
                  action: 'change_confirmed',
                  lastUpdated: Timestamp.now() as any
                }
              }
            };
          }
          return user;
        })
      );
      
      alert('Changes confirmed successfully');
    } catch (error) {
      console.error('Error confirming account changes:', error);
      setError('Failed to confirm changes. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to delete a user
  const deleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Find the user in our state
      const userToDelete = users.find(u => u.id === userId);
      if (!userToDelete) {
        throw new Error('User not found');
      }
      
      // Get the full user document including any subcollections
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        throw new Error('User document not found in Firestore');
      }
      
      // Get trading accounts if any
      const accountsSnapshot = await getDocs(collection(db, 'users', userId, 'tradingAccounts'));
      const accounts = accountsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Create archive document with all user data, timestamp, and who deleted it
      const archiveData = {
        userId: userId,
        userData: userDoc.data(),
        tradingAccounts: accounts,
        deletedAt: Timestamp.now(),
        deletedBy: currentUser?.uid || 'unknown',
        deletedByEmail: currentUser?.email || 'unknown'
      };
      
      // Save to archives collection
      const archiveRef = doc(db, 'deletedUsers', userId);
      await setDoc(archiveRef, archiveData);
      
      // Delete all subscriptions for each trading account first
      for (const account of accounts) {
        // Get all subscriptions for this account
        const accountRef = doc(db, 'users', userId, 'tradingAccounts', account.id);
        const subscriptionsSnapshot = await getDocs(collection(accountRef, 'subscriptions'));
        
        // Delete each subscription
        const subscriptionDeletePromises = subscriptionsSnapshot.docs.map(subscriptionDoc => 
          deleteDoc(doc(db, 'users', userId, 'tradingAccounts', account.id, 'subscriptions', subscriptionDoc.id))
        );
        
        // Wait for all subscription deletions to complete
        await Promise.all(subscriptionDeletePromises);
        
        // Then delete the trading account
        await deleteDoc(accountRef);
      }
      
      // Delete user from Firebase Authentication using Cloud Function
      const response = await fetch(
        `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/deleteUserAccount`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            uid: userId,
            requesterUid: currentUser?.uid,
            isTechnicalAdmin: userData?.isTechnicalAdmin
          }),
        }
      );
      
      if (!response.ok) {
        const errorData = await response.text();
        console.error('Error deleting user from Authentication:', errorData);
        // Continue with Firestore deletion even if Auth deletion fails
        // We'll log the error but not block the process
      }
      
      // After archiving and deleting subcollections, delete the user document from Firestore
      await deleteDoc(doc(db, 'users', userId));
      
      // Remove the user from the local state
      setUsers(users.filter(user => user.id !== userId));
      
      alert('User deleted and archived successfully');
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(`Failed to delete user: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Add state for editing notes
  const [editingNotes, setEditingNotes] = useState<{[userId: string]: string}>({});
  const [savingNotes, setSavingNotes] = useState<{[userId: string]: boolean}>({});

  // Function to handle notes change
  const handleNotesChange = (userId: string, value: string) => {
    setEditingNotes(prev => ({ ...prev, [userId]: value }));
  };

  // Function to save notes to Firestore
  const saveNotes = async (userId: string) => {
    setSavingNotes(prev => ({ ...prev, [userId]: true }));
    try {
      await updateDoc(doc(db, 'users', userId), {
        adminNotes: editingNotes[userId] || ''
      });
      // Optionally, update local state
      setUsers(prev => prev.map(user => user.id === userId ? {
        ...user,
        userData: {
          ...user.userData,
          adminNotes: editingNotes[userId] || ''
        }
      } : user));
    } catch (err) {
      alert('Failed to save notes.');
    } finally {
      setSavingNotes(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Inside AdminDashboard component, add local state for provisioning status
  const [provisioningAccountId, setProvisioningAccountId] = useLocalState<string | null>(null);
  const [provisioningResult, setProvisioningResult] = useLocalState<{success: boolean, message: string} | null>(null);
  
  // Add state for strategy subscription
  const [subscribingAccountId, setSubscribingAccountId] = useState<string | null>(null);
  const [subscriptionResult, setSubscriptionResult] = useState<{success: boolean, message: string} | null>(null);

  // Add the handler function
  const handleProvisionFullAccount = async (user: UserWithAccount, accountToProvision: AdminTradingAccount) => {
    if (!user.tradingAccounts || user.tradingAccounts.length === 0) return;
    
    const accountKey = getAccountKey(user.id, accountToProvision.id);
    
    setProvisioningAccountId(accountToProvision.id);
    setProvisioningResult(null);
    
    try {
      // First decrypt the password if not already decrypted
      let password = decryptedPasswords[accountKey];
      if (!password && accountToProvision.password) {
        try {
          password = decrypt(accountToProvision.password);
          // Store the decrypted password in state
          setDecryptedPasswords(prev => ({
            ...prev,
            [accountKey]: password
          }));
        } catch (error) {
          throw new Error('Failed to decrypt password. Please reveal the password first.');
        }
      }

      if (!password) {
        throw new Error('Please reveal the password first before provisioning.');
      }

      const response = await fetch(`https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/provisionFullAccount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          login: accountToProvision.accountNumber,
          password: password,
          serverName: accountToProvision.server || accountToProvision.serverName,
          accountName: accountToProvision.accountName,
          platform: accountToProvision.platform || 'mt5',
          userId: user.id,
          brokerType: accountToProvision.brokerType || 'xm',
        })
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Failed to provision account: ${errorData}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to provision account');
      }

      // Update the account in Firestore with the MetaAPI ID
      if (data.metaApiAccountId) {
        const accountRef = doc(db, 'users', user.id, 'tradingAccounts', accountToProvision.id);
        await updateDoc(accountRef, {
          metaApiAccountId: data.metaApiAccountId,
          isConnected: true,
          updatedAt: new Date()
        });
      }

      setProvisioningResult({
        success: true, 
        message: data.message || 'Account provisioned successfully!'
      });

      // Refresh users to get updated state
      await fetchUsers(true);

    } catch (err: any) {
      console.error('Error provisioning account:', err);
      setProvisioningResult({
        success: false,
        message: err?.message || 'Failed to provision account'
      });
    } finally {
      setProvisioningAccountId(null);
    }
  };

  // Add the new function after handleProvisionFullAccount
  const linkExistingStrategyToMetaApi = async (user: UserWithAccount, account: AdminTradingAccount) => {
    if (!account.metaApiAccountId || !account.strategies || !account.strategies[0]) return;
    
    setSubscribingAccountId(account.id);
    setSubscriptionResult(null);
    
    try {
      // Get the strategy details to get the metaApiId
      const strategyRef = doc(db, 'strategies', account.strategies[0].id);
      const strategySnap = await getDoc(strategyRef);
      
      if (!strategySnap.exists()) {
        throw new Error('Strategy not found');
      }

      const strategyData = strategySnap.data();
      if (!strategyData.metaApiId) {
        throw new Error('Strategy is not properly configured with MetaAPI ID');
      }

      const response = await fetch(
        `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/subscribeToStrategy`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            subscriberId: account.metaApiAccountId,
            strategyId: strategyData.metaApiId,
            tradeVolume: account.strategies[0].tradeVolume || 0.01,
            subscriberName: account.accountName
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to link strategy to MetaAPI');
      }

      setSubscriptionResult({success: true, message: 'Successfully linked strategy to MetaAPI'});
      // Refresh users to get updated state
      await fetchUsers(true);
    } catch (err: any) {
      console.error('Error linking strategy to MetaAPI:', err);
      setSubscriptionResult({success: false, message: err?.message || 'Failed to link strategy to MetaAPI'});
    } finally {
      setSubscribingAccountId(null);
    }
  };

  // Add function to handle subscription request approval
  const approveSubscriptionRequest = async (userId: string) => {
    if (!window.confirm('Are you sure you want to approve this subscription request?')) {
      return;
    }

    try {
      setIsLoading(true);
      
      // Get the user's data first
      const targetUser = users.find(u => u.id === userId);
      if (!targetUser) {
        throw new Error('User not found');
      }

      // Get the user's subscription requests
      const requestsRef = collection(db, 'users', userId, 'subscriptionRequests');
      const requestsSnapshot = await getDocs(query(requestsRef, where('status', '==', 'pending')));
      
      if (requestsSnapshot.empty) {
        alert('No pending subscription requests found.');
        return;
      }

      // Get the first pending request
      const request = requestsSnapshot.docs[0];
      
      // Update the request status to approved
      await updateDoc(doc(requestsRef, request.id), {
        status: 'approved',
        approvedAt: new Date(),
        approvedBy: currentUser?.uid
      });

      // Update the user's subscription count in their userData
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        'subscriptionCount': (targetUser.userData.subscriptionCount || 1) + 1
      });

      // Update account status
      await updateAccountStatus(
        userId,
        'subscription_approved',
        undefined,
        undefined,
        'Additional subscription request approved'
      );

      // Refresh users
      await fetchUsers(true);
      
      alert('Subscription request approved successfully!');
    } catch (error) {
      console.error('Error approving subscription request:', error);
      setError('Failed to approve subscription request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle server name editing
  const startEditingServer = (userId: string, accountId: string, currentServer: string) => {
    const accountKey = getAccountKey(userId, accountId);
    setEditingServers(prev => ({
      ...prev,
      [accountKey]: currentServer
    }));
  };

  // Function to save server name changes
  const saveServerName = async (userId: string, accountId: string) => {
    const accountKey = getAccountKey(userId, accountId);
    const newServerName = editingServers[accountKey];
    
    if (!newServerName?.trim()) {
      alert('Server name cannot be empty');
      return;
    }

    setSavingServers(prev => ({ ...prev, [accountKey]: true }));
    
    try {
      // Update in Firestore
      const accountRef = doc(db, 'users', userId, 'tradingAccounts', accountId);
      await updateDoc(accountRef, {
        server: newServerName.trim(),
        serverName: newServerName.trim(),
        updatedAt: new Date()
      });

      // Update local state
      setUsers(prevUsers => 
        prevUsers.map(user => {
          if (user.id === userId) {
            return {
              ...user,
              tradingAccounts: user.tradingAccounts.map(acc => {
                if (acc.id === accountId) {
                  return {
                    ...acc,
                    server: newServerName.trim(),
                    serverName: newServerName.trim()
                  };
                }
                return acc;
              })
            };
          }
          return user;
        })
      );

      // Clear editing state
      setEditingServers(prev => {
        const newState = { ...prev };
        delete newState[accountKey];
        return newState;
      });

      alert('Server name updated successfully');
    } catch (error) {
      console.error('Error updating server name:', error);
      alert('Failed to update server name. Please try again.');
    } finally {
      setSavingServers(prev => ({ ...prev, [accountKey]: false }));
    }
  };

  // Function to cancel server editing
  const cancelEditingServer = (userId: string, accountId: string) => {
    const accountKey = getAccountKey(userId, accountId);
    setEditingServers(prev => {
      const newState = { ...prev };
      delete newState[accountKey];
      return newState;
    });
  };

  // Function to handle account number editing
  const startEditingAccountNumber = (userId: string, accountId: string, currentAccountNumber: string) => {
    const accountKey = getAccountKey(userId, accountId);
    setEditingAccountNumbers(prev => ({
      ...prev,
      [accountKey]: currentAccountNumber
    }));
  };

  // Function to save account number changes
  const saveAccountNumber = async (userId: string, accountId: string) => {
    const accountKey = getAccountKey(userId, accountId);
    const newAccountNumber = editingAccountNumbers[accountKey];
    
    if (!newAccountNumber?.trim()) {
      alert('Account number cannot be empty');
      return;
    }

    setSavingAccountNumbers(prev => ({ ...prev, [accountKey]: true }));
    
    try {
      // Update in Firestore
      const accountRef = doc(db, 'users', userId, 'tradingAccounts', accountId);
      await updateDoc(accountRef, {
        accountNumber: newAccountNumber.trim(),
        updatedAt: new Date()
      });

      // Update local state
      setUsers(prevUsers => 
        prevUsers.map(user => {
          if (user.id === userId) {
            return {
              ...user,
              tradingAccounts: user.tradingAccounts.map(acc => {
                if (acc.id === accountId) {
                  return {
                    ...acc,
                    accountNumber: newAccountNumber.trim()
                  };
                }
                return acc;
              })
            };
          }
          return user;
        })
      );

      // Clear editing state
      setEditingAccountNumbers(prev => {
        const newState = { ...prev };
        delete newState[accountKey];
        return newState;
      });

      alert('Account number updated successfully');
    } catch (error) {
      console.error('Error updating account number:', error);
      alert('Failed to update account number. Please try again.');
    } finally {
      setSavingAccountNumbers(prev => ({ ...prev, [accountKey]: false }));
    }
  };

  // Function to cancel account number editing
  const cancelEditingAccountNumber = (userId: string, accountId: string) => {
    const accountKey = getAccountKey(userId, accountId);
    setEditingAccountNumbers(prev => {
      const newState = { ...prev };
      delete newState[accountKey];
      return newState;
    });
  };

  if (!currentUser || !userData?.isAdmin) {
    return <Navigate to="/dashboard" />;
  }

  // Top container styling
  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <h1 className="text-xl font-bold">Admin Dashboard</h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {userData?.isTechnicalAdmin && (
                <Link
                  to="/admin/settings"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Technical Settings
                </Link>
              )}
              <button
                onClick={handleSignOut}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header with Back button and title */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleBackToDashboard}
              className="text-gray-600 hover:text-gray-900 flex items-center transition-colors duration-200"
            >
              <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
              Back to Dashboard
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            {/* Removed real-time listener indicator since the functionality was removed */}
            <button 
              onClick={() => {
                // Force a refresh by fetching users again
                fetchUsers(true);
              }}
              className="ml-4 px-3 py-1 bg-blue-50 text-blue-600 text-xs rounded-md hover:bg-blue-100 transition-colors duration-200 flex items-center"
              title="Force refresh data"
            >
              <svg className="h-3 w-3 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
          <div className="text-sm text-gray-500">
            {filteredUsers.length} {brokerFilter !== 'all' && <span>Filtered</span>} Users · {new Date().toLocaleDateString()}
          </div>
        </div>
        
        {/* Search bar with icons and broker filter */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-grow">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search users by name, email, or mobile..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-20 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-t1blue focus:border-transparent transition duration-150 ease-in-out"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 space-x-2">
              <button
                onClick={() => fetchUsers(true)}
                className="text-gray-400 hover:text-gray-600 focus:outline-none transition-colors duration-200 flex items-center"
                title="Refresh user list"
                disabled={isLoading}
              >
                <svg className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              </div>
            </div>
            
            {/* Broker Filter Dropdown */}
            <div className="md:w-64">
              <div className="relative">
                <select
                  id="broker-filter"
                  value={brokerFilter}
                  onChange={(e) => setBrokerFilter(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-t1blue focus:border-t1blue sm:text-sm rounded-md"
                >
                  <option value="all">All Brokers</option>
                  <option value={BrokerType.XM}>XM Global</option>
                  <option value={BrokerType.WELTRADE}>Weltrade</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>
            
            {/* MetaAPI Filter Dropdown */}
            <div className="md:w-64">
              <div className="relative">
                <select
                  id="metaapi-filter"
                  value={metaApiFilter}
                  onChange={(e) => setMetaApiFilter(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-t1blue focus:border-t1blue sm:text-sm rounded-md"
                >
                  <option value="all">All Accounts</option>
                  <option value="metaapi">MetaAPI Accounts</option>
                  <option value="non-metaapi">Non-MetaAPI Accounts</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setActiveTab('noSubscription')}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center ${
                    activeTab === 'noSubscription'
                ? 'bg-white text-gray-800 shadow-sm' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              No Subscription
                  <span className="ml-2 bg-gray-200 text-gray-700 py-0.5 px-2 rounded-full text-xs">
                {tabCounts.noSubscription}
                  </span>
                </button>
                <button
                  onClick={() => setActiveTab('withSubscription')}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center ${
                    activeTab === 'withSubscription'
                ? 'bg-white text-gray-800 shadow-sm' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              With Subscription
                  <span className="ml-2 bg-gray-200 text-gray-700 py-0.5 px-2 rounded-full text-xs">
                {tabCounts.withSubscription}
                  </span>
                </button>
                <button
              onClick={() => setActiveTab('activeTraders')}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center ${
                    activeTab === 'activeTraders'
                ? 'bg-white text-gray-800 shadow-sm' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
                  Active Traders
                  <span className="ml-2 bg-gray-200 text-gray-700 py-0.5 px-2 rounded-full text-xs">
                {tabCounts.activeTraders}
                  </span>
                </button>
            <button
              onClick={() => setActiveTab('disconnected')}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center ${
                activeTab === 'disconnected' 
                ? 'bg-blue-50 text-blue-600 shadow-sm border border-blue-200' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Disconnected
              <span className="ml-2 bg-blue-100 text-blue-700 py-0.5 px-2 rounded-full text-xs">
                {tabCounts.disconnected}
              </span>
            </button>
                <button
                  onClick={() => setActiveTab('admins')}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center ${
                    activeTab === 'admins'
                ? 'bg-white text-gray-800 shadow-sm' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Admins
                  <span className="ml-2 bg-gray-200 text-gray-700 py-0.5 px-2 rounded-full text-xs">
                {tabCounts.admins}
                  </span>
                </button>
            <button
              onClick={() => setActiveTab('changes')}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center ${
                activeTab === 'changes' 
                ? 'bg-white text-gray-800 shadow-sm' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Changes
              <span className="ml-2 bg-gray-200 text-gray-700 py-0.5 px-2 rounded-full text-xs">
                {tabCounts.changes}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center ${
                activeTab === 'requests' 
                ? 'bg-blue-50 text-blue-600 shadow-sm border border-blue-200' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Subscription Requests
              <span className="ml-2 bg-blue-100 text-blue-700 py-0.5 px-2 rounded-full text-xs">
                {tabCounts.requests}
              </span>
            </button>
          </div>
        </div>

        {/* Tab description */}
        {activeTab === 'disconnected' && (
          <div className="mb-6">
            <div className="rounded-lg bg-blue-50 p-4 flex items-center border border-blue-100">
              <svg className="h-6 w-6 text-blue-500 mr-3 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-grow">
                <p className="text-sm text-blue-700">
                  These users have active subscriptions and trading accounts but are not connected to the trading system.
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'changes' && (
          <div className="mb-6">
            <div className="rounded-lg bg-yellow-50 p-4 flex items-center border border-yellow-100">
              <svg className="h-6 w-6 text-yellow-500 mr-3 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-grow">
                <p className="text-sm text-yellow-700">
                  These users have recent account changes that need to be reviewed and confirmed by an admin.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Add active filters indicator if a broker filter is applied */}
        {brokerFilter !== 'all' && (
          <div className="mb-4 flex items-center">
            <span className="text-sm text-gray-600 mr-2">Active Filter:</span>
            <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-medium flex items-center">
              {normalizeBrokerType(brokerFilter) === normalizeBrokerType(BrokerType.XM) ? 'XM Global' : 'Deriv'}
              <button 
                onClick={() => setBrokerFilter('all')} 
                className="ml-1 text-blue-600 hover:text-blue-800 focus:outline-none"
                title="Clear filter"
              >
                <svg className="h-3 w-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </span>
        </div>
        )}

        {/* Add MetaAPI filter indicator */}
        {metaApiFilter !== 'all' && (
          <div className="mb-4 flex items-center">
            <span className="text-sm text-gray-600 mr-2">MetaAPI Filter:</span>
            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium flex items-center">
              {metaApiFilter === 'metaapi' ? 'MetaAPI Accounts' : 'Non-MetaAPI Accounts'}
              <button 
                onClick={() => setMetaApiFilter('all')} 
                className="ml-1 text-green-600 hover:text-green-800 focus:outline-none"
                title="Clear MetaAPI filter"
              >
                <svg className="h-3 w-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </span>
          </div>
        )}

        {/* Grid layout for cards instead of table */}
          {isLoading && !isAddingSubscription ? (
            <div className="border-t border-gray-200 px-6 py-16 text-center">
              <div className="flex justify-center">
                <svg className="animate-spin h-10 w-10 text-t1blue" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <p className="mt-4 text-gray-500 text-lg">Loading users...</p>
            </div>
        ) : tabFilteredUsers.length === 0 ? (
          <div className="border-t border-gray-200 px-6 py-16 text-center">
                        <div className="flex flex-col items-center">
                          <svg className="h-12 w-12 text-gray-300 mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
              <p className="text-lg font-medium">
                {searchQuery ? 'No users match your search.' : 
                 brokerFilter !== 'all' ? `No users found with broker type: ${normalizeBrokerType(brokerFilter) === normalizeBrokerType(BrokerType.XM) ? 'XM Global' : 'Weltrade'}` : 
                 metaApiFilter !== 'all' ? `No users found with ${metaApiFilter === 'metaapi' ? 'MetaAPI accounts' : 'non-MetaAPI accounts'}` :
                 'No users found in this category.'}
              </p>
              <p className="text-sm mt-1">
                {searchQuery ? 'Try a different search term.' : 
                 brokerFilter !== 'all' ? 'Try changing the broker filter or check that users have the selected broker type.' :
                 metaApiFilter !== 'all' ? 'Try changing the MetaAPI filter or check that users have the correct account type.' :
                 'Users will appear here when available.'}
              </p>
              {(brokerFilter !== 'all' || metaApiFilter !== 'all') && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {brokerFilter !== 'all' && (
                    <button 
                      onClick={() => setBrokerFilter('all')} 
                      className="px-4 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                    >
                      Clear Broker Filter
                    </button>
                  )}
                  {metaApiFilter !== 'all' && (
                    <button 
                      onClick={() => setMetaApiFilter('all')} 
                      className="px-4 py-2 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
                    >
                      Clear MetaAPI Filter
                    </button>
                  )}
                </div>
              )}
                        </div>
                            </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
            {tabFilteredUsers.map(user => (
              <div 
                key={user.id} 
                className={`bg-white rounded-lg shadow ${
                  user.userData.isAdmin ? 'border-l-4 border-purple-400' : 
                  user.tradingAccounts.some(acc => !acc.isConnected) ? 'border-l-4 border-red-400' : ''
                }`}
              >
                <div className="p-6">
                  {/* User header */}
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {highlightText(`${user.userData.firstName} ${user.userData.lastName}`, searchQuery)}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {highlightText(user.userData.email, searchQuery)}
                      </p>
                      {user.userData.mobile && (
                        <p className="text-sm text-gray-500">
                          {highlightText(user.userData.mobile, searchQuery)}
                        </p>
                      )}
                      {/* Add subscription count */}
                      <div className="mt-2 flex items-center">
                        <span className="text-sm text-gray-600 mr-2">Subscriptions:</span>
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {user.userData.subscriptionCount || 1}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {user.userData.isAdmin && (
                        <span className="px-2 py-1 text-xs font-medium rounded bg-purple-100 text-purple-800">
                          Admin
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Trading Accounts Section */}
                  <div className="space-y-4">
                    {user.tradingAccounts.map((account, index) => (
                      <div 
                        key={account.id} 
                        className={`bg-gray-50/70 border border-gray-100 rounded-lg p-4 hover:bg-gray-50/90 transition-colors duration-200 ${
                          !account.isConnected ? 'border-l-4 border-l-red-400' : 
                          account.metaApiAccountId ? 'border-l-4 border-l-blue-400' : ''
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="space-y-1">
                            <div className="flex items-center space-x-2">
                              <h4 className="text-md font-medium text-gray-900">{account.accountName}</h4>
                              <button
                                onClick={() => copyToClipboard(account.accountName, `name-${account.id}`)}
                                className="text-gray-400 hover:text-gray-600 focus:outline-none"
                                title="Copy account name"
                              >
                                {copiedInfo[`name-${account.id}`] ? (
                                  <svg className="h-4 w-4 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : (
                                  <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                                  </svg>
                                )}
                              </button>
                            </div>
                            <div className="flex items-center space-x-2">
                              {(() => {
                                const accountKey = getAccountKey(user.id, account.id);
                                const isEditingNumber = editingAccountNumbers[accountKey] !== undefined;
                                
                                // Only allow editing for non-MetaAPI accounts
                                if (isEditingNumber && !account.metaApiAccountId) {
                                  return (
                                    <div className="flex items-center space-x-2 bg-white px-2 py-0.5 rounded border border-gray-200">
                                      <span className="text-sm text-gray-500 font-medium">#</span>
                                      <input
                                        type="text"
                                        value={editingAccountNumbers[accountKey]}
                                        onChange={(e) => setEditingAccountNumbers(prev => ({
                                          ...prev,
                                          [accountKey]: e.target.value
                                        }))}
                                        className="text-sm text-gray-600 bg-transparent border border-gray-300 rounded px-1 py-0 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 min-w-0 flex-1"
                                        autoFocus
                                      />
                                      <button
                                        onClick={() => saveAccountNumber(user.id, account.id)}
                                        disabled={savingAccountNumbers[accountKey]}
                                        className="text-green-600 hover:text-green-800 focus:outline-none disabled:opacity-50"
                                        title="Save account number"
                                      >
                                        {savingAccountNumbers[accountKey] ? (
                                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                          </svg>
                                        ) : (
                                          <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                          </svg>
                                        )}
                                      </button>
                                      <button
                                        onClick={() => cancelEditingAccountNumber(user.id, account.id)}
                                        className="text-gray-400 hover:text-gray-600 focus:outline-none"
                                        title="Cancel editing"
                                      >
                                        <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                      </button>
                                    </div>
                                  );
                                } else {
                                  return (
                                    <div className="flex items-center space-x-2">
                                      <span className="text-sm text-gray-500 bg-white px-2 py-0.5 rounded border border-gray-200">
                                        #{account.accountNumber}
                                      </span>
                                      <button
                                        onClick={() => copyToClipboard(account.accountNumber, `number-${account.id}`)}
                                        className="text-gray-400 hover:text-gray-600 focus:outline-none"
                                        title="Copy account number"
                                      >
                                        {copiedInfo[`number-${account.id}`] ? (
                                          <svg className="h-4 w-4 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                          </svg>
                                        ) : (
                                          <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                                          </svg>
                                        )}
                                      </button>
                                      {/* Only show edit button for non-MetaAPI accounts and if user is admin */}
                                      {userData?.isAdmin && !account.metaApiAccountId && (
                                        <button
                                          onClick={() => startEditingAccountNumber(user.id, account.id, account.accountNumber)}
                                          className="text-gray-400 hover:text-gray-600 focus:outline-none"
                                          title="Edit account number"
                                        >
                                          <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                          </svg>
                                        </button>
                                      )}
                                    </div>
                                  );
                                }
                              })()}
                            </div>
                            <div className="flex items-center space-x-2">
                              {(() => {
                                const accountKey = getAccountKey(user.id, account.id);
                                const isEditingServer = editingServers[accountKey] !== undefined;
                                
                                if (isEditingServer) {
                                  return (
                                    <div className="flex items-center space-x-2 bg-white px-2 py-0.5 rounded border border-gray-200">
                                      <span className="text-sm text-gray-500 font-medium">Server:</span>
                                      <input
                                        type="text"
                                        value={editingServers[accountKey]}
                                        onChange={(e) => setEditingServers(prev => ({
                                          ...prev,
                                          [accountKey]: e.target.value
                                        }))}
                                        className="text-sm text-gray-600 bg-transparent border border-gray-300 rounded px-1 py-0 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 min-w-0 flex-1"
                                        autoFocus
                                      />
                                      <button
                                        onClick={() => saveServerName(user.id, account.id)}
                                        disabled={savingServers[accountKey]}
                                        className="text-green-600 hover:text-green-800 focus:outline-none disabled:opacity-50"
                                        title="Save server name"
                                      >
                                        {savingServers[accountKey] ? (
                                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                          </svg>
                                        ) : (
                                          <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                          </svg>
                                        )}
                                      </button>
                                      <button
                                        onClick={() => cancelEditingServer(user.id, account.id)}
                                        className="text-gray-400 hover:text-gray-600 focus:outline-none"
                                        title="Cancel editing"
                                      >
                                        <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                      </button>
                                    </div>
                                  );
                                } else {
                                  return (
                                    <div className="flex items-center space-x-2">
                                      <span className="text-sm text-gray-500">
                                        <span className="font-medium">Server:</span> {account.serverName}
                                      </span>
                                      {userData?.isAdmin && (
                                        <button
                                          onClick={() => startEditingServer(user.id, account.id, account.serverName || '')}
                                          className="text-gray-400 hover:text-gray-600 focus:outline-none"
                                          title="Edit server name"
                                        >
                                          <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                          </svg>
                                        </button>
                                      )}
                                    </div>
                                  );
                                }
                              })()}
                            </div>
                            {/* Add password field */}
                            <div className="mt-1 flex items-center space-x-2">
                              {/* Password field - Only visible for technical admins */}
                              {userData?.isTechnicalAdmin && (
                                <div className="mt-1 flex items-center space-x-2 bg-white px-2 py-0.5 rounded border border-gray-200">
                                  {/* Generate the accountKey for this section */}
                                  {(() => {
                                    const accountKey = getAccountKey(user.id, account.id);
                                    return (
                                      <>
                                        <div className="relative flex-grow">
                                          <input
                                            type={visiblePasswords[accountKey] ? 'text' : 'password'}
                                            value={decryptedPasswords[accountKey] || '••••••••'}
                                            readOnly
                                            className="text-sm text-gray-600 bg-transparent border-none p-0 focus:ring-0 cursor-default"
                                          />
                                        </div>
                                        <button
                                          onClick={() => togglePasswordVisibility(user.id, account.id)}
                                          className="text-gray-400 hover:text-gray-600 focus:outline-none"
                                          title={visiblePasswords[accountKey] ? 'Hide password' : 'Show password'}
                                        >
                                          {decryptingPasswords[accountKey] ? (
                                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                          ) : visiblePasswords[accountKey] ? (
                                            <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                            </svg>
                                          ) : (
                                            <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                          )}
                                        </button>
                                        {decryptedPasswords[accountKey] && (
                                          <button
                                            onClick={() => copyToClipboard(decryptedPasswords[accountKey], accountKey)}
                                            className="text-gray-400 hover:text-gray-600 focus:outline-none"
                                            title="Copy password"
                                          >
                                            {copiedInfo[accountKey] ? (
                                              <svg className="h-4 w-4 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                              </svg>
                                            ) : (
                                              <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                                              </svg>
                                            )}
                                          </button>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {account.metaApiAccountId && (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                MetaAPI
                              </span>
                            )}
                            {account.isConnected ? (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                Connected
                              </span>
                            ) : (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                                Disconnected
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Strategies for this account */}
                        {account.strategies && account.strategies.length > 0 && (
                          <div className="mt-3 space-y-2">
                            <h5 className="text-xs font-medium text-gray-700">Active Strategies</h5>
                            {account.strategies.map(strategy => (
                              <div key={strategy.id} className="bg-white rounded p-2 flex justify-between items-center border border-gray-200">
                                <div>
                                  <span className="text-sm font-medium text-gray-900">{strategy.name}</span>
                                  <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">Lot Size: {strategy.tradeVolume}</span>
                                </div>
                                <button
                                  onClick={() => removeFromStrategy(user.id, account.id, strategy.subscriptionId)}
                                  className="text-xs text-red-600 hover:text-red-800"
                                >
                                  Remove
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Account Actions */}
                        <div className="mt-3 flex flex-wrap gap-2">
                          {/* Provision to MetaAPI button - Only visible for technical admins */}
                          {userData?.isTechnicalAdmin && !account.metaApiAccountId && (
                            <button
                              onClick={() => handleProvisionFullAccount(user, account)}
                              className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-600 hover:bg-blue-200 flex items-center space-x-1"
                              disabled={!decryptedPasswords[getAccountKey(user.id, account.id)]}
                            >
                              {provisioningAccountId === account.id ? (
                                <>
                                  <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  <span>Provisioning...</span>
                                </>
                              ) : (
                                <>
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                  </svg>
                                  <span>Provision to MetaAPI</span>
                                </>
                              )}
                            </button>
                          )}
                          {/* Toggle Connection button - Only visible for non-MetaAPI accounts */}
                          {!account.metaApiAccountId && (
                            <button
                              onClick={() => toggleConnectionStatus(user.id, account.id)}
                              className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
                            >
                              Toggle Connection
                            </button>
                          )}
                          <button
                            onClick={() => deleteUserAccount(user.id, account.id)}
                            className="px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-600 hover:bg-red-200"
                          >
                            Delete Account
                          </button>
                          {!account.strategies?.length && (
                            <button
                              onClick={() => handleManageSubscription(user)}
                              className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-600 hover:bg-blue-200"
                            >
                              Add Strategy
                            </button>
                          )}
                          {/* Add Link Strategy to MetaAPI button */}
                          {account.metaApiAccountId && account.strategies && account.strategies.length > 0 && (
                            <button
                              onClick={() => linkExistingStrategyToMetaApi(user, account)}
                              className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-600 hover:bg-green-200 flex items-center space-x-1"
                            >
                              {subscribingAccountId === account.id ? (
                                <>
                                  <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  <span>Linking Strategy...</span>
                                </>
                              ) : (
                                <>
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                  </svg>
                                  <span>Link Strategy to MetaAPI</span>
                                </>
                              )}
                            </button>
                          )}
                          {/* Show subscription result if any */}
                          {subscribingAccountId === account.id && subscriptionResult && (
                            <div className={`mt-2 p-2 text-xs rounded ${
                              subscriptionResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                            }`}>
                              {subscriptionResult.message}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Add Account Button */}
                  {user.userData.subscription?.status === 'active' && (
                    <div className="mt-4">
                      <button
                        onClick={() => handleAddAccount(user)}
                        className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded shadow-sm hover:bg-blue-700 transition-colors duration-150"
                      >
                        Add Trading Account
                      </button>
                    </div>
                  )}

                  {/* User Actions */}
                  <div className="mt-4 border-t border-gray-100 pt-4">
                    {/* Action Groups Container */}
                    <div className="space-y-3">
                      {/* Subscription Management Group */}
                      {(!user.userData.subscription || user.userData.subscription?.status !== 'active') && (
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 mb-2">Subscription</h4>
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => handleAddSubscription(user.id)}
                              className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded shadow-sm hover:bg-blue-700 transition-colors duration-150"
                            >
                              <svg className="h-3.5 w-3.5 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                              </svg>
                              Add Subscription
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Active Subscription Actions */}
                      {user.userData.subscription && (
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 mb-2">Subscription Management</h4>
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => handleEditSubscription(user.id, user.userData.subscription)}
                              className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded hover:bg-gray-200 transition-colors duration-150"
                            >
                              <svg className="h-3.5 w-3.5 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                              </svg>
                              Edit Subscription
                            </button>
                            {user.userData.subscription.status === 'active' && (
                              <button
                                onClick={() => cancelSubscription(user.id)}
                                className="inline-flex items-center px-3 py-1.5 bg-red-100 text-red-600 text-xs font-medium rounded hover:bg-red-200 transition-colors duration-150"
                              >
                                <svg className="h-3.5 w-3.5 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                                Cancel
                              </button>
                            )}
                            {user.userData.subscription.status === 'canceled' && (
                              <button
                                onClick={() => reactivateSubscription(user.id)}
                                className="inline-flex items-center px-3 py-1.5 bg-green-100 text-green-600 text-xs font-medium rounded hover:bg-green-200 transition-colors duration-150"
                              >
                                <svg className="h-3.5 w-3.5 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Reactivate
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Trading Account Management */}
                      {user.userData.subscription?.status === 'active' && (
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 mb-2">Trading Account</h4>
                          <div className="flex flex-wrap gap-2">
                            {!user.tradingAccounts?.length && (
                              <button
                                onClick={() => handleAddAccount(user)}
                                className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded shadow-sm hover:bg-blue-700 transition-colors duration-150"
                              >
                                <svg className="h-3.5 w-3.5 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                                </svg>
                                Add Account
                              </button>
                            )}
                            {user.tradingAccounts?.length > 0 && !user.hasActiveStrategy && (
                              <button
                                onClick={() => handleManageSubscription(user)}
                                className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded shadow-sm hover:bg-blue-700 transition-colors duration-150"
                              >
                                <svg className="h-3.5 w-3.5 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
                                </svg>
                                Add Strategy
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Admin Controls */}
                      <div>
                        <h4 className="text-xs font-medium text-gray-500 mb-2">Admin Controls</h4>
                        <div className="flex flex-wrap gap-2">
                          {!user.userData.isAdmin ? (
                            <button
                              onClick={() => toggleAdminStatus(user)}
                              className="inline-flex items-center px-3 py-1.5 bg-purple-100 text-purple-600 text-xs font-medium rounded hover:bg-purple-200 transition-colors duration-150"
                              disabled={currentUser?.uid === user.id}
                            >
                              <svg className="h-3.5 w-3.5 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
                              </svg>
                              Make Admin
                            </button>
                          ) : (
                            <button
                              onClick={() => toggleAdminStatus(user)}
                              className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-medium rounded hover:bg-gray-200 transition-colors duration-150"
                              disabled={currentUser?.uid === user.id}
                            >
                              <svg className="h-3.5 w-3.5 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
                              </svg>
                              Revoke Admin
                            </button>
                          )}

                          {/* Technical Admin Delete User Button */}
                          {userData?.isTechnicalAdmin && currentUser?.uid !== user.id && (
                            <button
                              onClick={() => deleteUser(user.id)}
                              className="inline-flex items-center px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded shadow-sm hover:bg-red-700 transition-colors duration-150"
                            >
                              <svg className="h-3.5 w-3.5 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              Delete User
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Subscription Request Approval */}
                      {activeTab === 'requests' && user.userData.accountStatus?.action === 'subscription_requested' && (
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 mb-2">Pending Request</h4>
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => approveSubscriptionRequest(user.id)}
                              className="inline-flex items-center px-3 py-1.5 bg-green-100 text-green-700 text-xs font-medium rounded hover:bg-green-200 transition-colors duration-150"
                            >
                              <svg className="h-3.5 w-3.5 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              Approve Additional Subscription
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Change Confirmation */}
                      {activeTab === 'changes' && user.userData.accountStatus?.action && user.userData.accountStatus.action !== 'change_confirmed' && (
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 mb-2">Changes</h4>
                          {/* Add change comment display */}
                          {user.userData.accountStatus?.details && (
                            <div className="mb-3 p-3 bg-yellow-50 border border-yellow-100 rounded-md">
                              <div className="flex items-start">
                                <svg className="h-5 w-5 text-yellow-400 mr-2 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                <div>
                                  <p className="text-sm text-yellow-700 font-medium">Change Details:</p>
                                  <p className="text-sm text-yellow-600 mt-1">{user.userData.accountStatus.details}</p>
                                  {user.userData.accountStatus.lastUpdated && (
                                    <p className="text-xs text-yellow-500 mt-1">
                                      Changed on: {new Date(user.userData.accountStatus.lastUpdated.seconds * 1000).toLocaleString()}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => confirmAccountChanges(user.id)}
                              className="inline-flex items-center px-3 py-1.5 bg-green-100 text-green-700 text-xs font-medium rounded hover:bg-green-200 transition-colors duration-150"
                            >
                              <svg className="h-3.5 w-3.5 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              Confirm Changes
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* After the action buttons in the user card, add the notes section */}
                  <div className="mt-4">
                    <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Admin Notes</h4>
                    <textarea
                      className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-t1blue focus:border-t1blue"
                      rows={3}
                      placeholder="Add notes for this user..."
                      value={editingNotes[user.id] !== undefined ? editingNotes[user.id] : (user.userData.adminNotes || '')}
                      onChange={e => handleNotesChange(user.id, e.target.value)}
                      disabled={savingNotes[user.id] || !userData?.isAdmin}
                      readOnly={!userData?.isAdmin}
                    />
                    {userData?.isAdmin && (
                      <button
                        onClick={() => saveNotes(user.id)}
                        disabled={savingNotes[user.id]}
                        className="mt-2 px-3 py-1.5 bg-blue-100 text-blue-700 text-xs font-medium rounded hover:bg-blue-200 transition-colors duration-150"
                      >
                        {savingNotes[user.id] ? 'Saving...' : 'Save Notes'}
                      </button>
          )}
        </div>
      </div>
              </div>
            ))}
          </div>
        )}

      {/* Subscription Modal */}
      {isAddingSubscription && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative p-6 border w-full max-w-md shadow-2xl rounded-xl bg-white transition-all transform duration-300 ease-in-out">
            <div className="absolute top-0 right-0 pt-4 pr-4">
              <button
                onClick={() => {
                  setIsAddingSubscription(false);
                  setIsEditingSubscription(false);
                  setSelectedUserId(null);
                }}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="mb-5">
              <h3 className="text-xl font-bold text-gray-900">
                {isEditingSubscription ? 'Edit Subscription' : 'Add Subscription'}
              </h3>
              <p className="text-gray-500 text-sm mt-1">
                {isEditingSubscription ? 'Update subscription details for this user' : 'Give this user access to the platform'}
              </p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Plan
                </label>
                <select
                  value={newSubscription.planId}
                  onChange={(e) => {
                    const plan = planOptions.find(p => p.id === e.target.value);
                    if (plan) {
                      setNewSubscription(prev => ({
                        ...prev,
                        planId: plan.id,
                        planName: plan.name
                      }));
                    }
                  }}
                  className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-t1blue focus:border-t1blue sm:text-sm"
                >
                  {planOptions.map(plan => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Status
                </label>
                <select
                  value={newSubscription.status}
                  onChange={(e) => setNewSubscription(prev => ({ ...prev, status: e.target.value }))}
                  className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-t1blue focus:border-t1blue sm:text-sm"
                >
                  <option value="active">Active</option>
                  <option value="expired">Expired</option>
                  <option value="canceled">Canceled</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Duration (days)
                </label>
                <input
                  type="number"
                  value={newSubscription.duration}
                  onChange={(e) => setNewSubscription(prev => ({ ...prev, duration: parseInt(e.target.value) || 0 }))}
                  className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-t1blue focus:border-t1blue sm:text-sm"
                  min="1"
                />
              </div>

              <div className="flex items-center">
                <input
                  id="trial-checkbox"
                  type="checkbox"
                  checked={newSubscription.isTrial}
                  onChange={(e) => setNewSubscription(prev => ({ ...prev, isTrial: e.target.checked }))}
                  className="h-4 w-4 text-t1blue focus:ring-t1blue border-gray-300 rounded"
                />
                <label htmlFor="trial-checkbox" className="ml-2 block text-sm text-gray-700">
                  Include Trial Period
                </label>
              </div>

              {newSubscription.isTrial && (
                <div className="ml-6 pl-2 border-l-2 border-t1blue">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Trial Duration (days)
                  </label>
                  <input
                    type="number"
                    value={newSubscription.trialDuration}
                    onChange={(e) => setNewSubscription(prev => ({ ...prev, trialDuration: parseInt(e.target.value) || 0 }))}
                    className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-t1blue focus:border-t1blue sm:text-sm"
                    min="1"
                  />
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setIsAddingSubscription(false);
                  setIsEditingSubscription(false);
                  setSelectedUserId(null);
                }}
                className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-t1blue transition-colors duration-150"
              >
                Cancel
              </button>
              <button
                onClick={saveSubscription}
                className="px-4 py-2 bg-t1blue text-white text-sm font-medium rounded-md shadow-sm hover:bg-t1blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-t1blue transition-colors duration-150"
              >
                {isEditingSubscription ? 'Update' : 'Add'} Subscription
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Strategy Subscription Modal */}
      {isManagingSubscription && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative p-6 border w-full max-w-md shadow-2xl rounded-xl bg-white transition-all transform duration-300 ease-in-out">
            <div className="absolute top-0 right-0 pt-4 pr-4">
              <button
                onClick={() => {
                  setIsManagingSubscription(false);
                  setSelectedUserForSubscription(null);
                  setSelectedStrategy('');
                  setTradeVolume(0.01);
                  setSubscriptionSuccess('');
                  setSubscriptionError('');
                }}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="mb-5">
              <h3 className="text-xl font-bold text-gray-900">
                {selectedUserForSubscription?.hasActiveStrategy ? 'Change Strategy' : 'Add Strategy'}
              </h3>
              <p className="text-gray-500 text-sm mt-1">
                {selectedUserForSubscription?.hasActiveStrategy ? 'Change the trading strategy for this user' : 'Subscribe this user to a trading strategy'}
              </p>
            </div>
            {isLoadingStrategies ? (
              <div className="flex justify-center items-center py-12">
                <svg className="animate-spin h-8 w-8 text-t1blue" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Select Strategy
                  </label>
                  <select
                    value={selectedStrategy}
                    onChange={(e) => setSelectedStrategy(e.target.value)}
                    className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-t1blue focus:border-t1blue sm:text-sm"
                  >
                    <option value="">Select a strategy...</option>
                    {strategies.map(strategy => (
                      <option key={strategy.id} value={strategy.id}>
                        {strategy.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Trade Volume
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <input
                      type="number"
                      value={tradeVolume}
                      onChange={(e) => setTradeVolume(parseFloat(e.target.value) || 0)}
                      step="0.01"
                      min="0.01"
                      className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-t1blue focus:border-t1blue sm:text-sm"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Minimum value: 0.01 (standard lot)
                  </p>
                </div>

                {subscriptionError && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-700">{subscriptionError}</p>
                      </div>
                    </div>
                  </div>
                )}

                {subscriptionSuccess && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-green-700">{subscriptionSuccess}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setIsManagingSubscription(false);
                  setSelectedUserForSubscription(null);
                  setSelectedStrategy('');
                  setTradeVolume(0.01);
                  setSubscriptionSuccess('');
                  setSubscriptionError('');
                }}
                className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-t1blue transition-colors duration-150"
              >
                Cancel
              </button>
              <button
                onClick={subscribeUserToStrategy}
                disabled={isLoadingStrategies || !selectedStrategy}
                className="px-4 py-2 bg-t1blue text-white text-sm font-medium rounded-md shadow-sm hover:bg-t1blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-t1blue transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoadingStrategies ? 'Loading...' : 'Subscribe'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Account Modal */}
      {isAddingAccount && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative p-6 border w-full max-w-md shadow-2xl rounded-xl bg-white transition-all transform duration-300 ease-in-out">
            <div className="absolute top-0 right-0 pt-4 pr-4">
              <button
                onClick={() => {
                  setIsAddingAccount(false);
                  setSelectedUserForAccount(null);
                  setAccountNumber('');
                  setMetaAccountName('');
                  setMetaPassword('');
                  setSelectedServer('');
                  setCustomServerName('');
                  setUseCustomServer(false);
                  setVerificationStatus(VerificationStatus.IDLE);
                  setVerificationMessage('');
                  setAccountError('');
                  setAccountSuccess('');
                }}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="mb-5">
              <h3 className="text-xl font-bold text-gray-900">
                Add Trading Account
              </h3>
              <p className="text-gray-500 text-sm mt-1">
                Connect {selectedUserForAccount?.userData.firstName}'s XM trading account
              </p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Account Name
                </label>
                <input
                  type="text"
                  value={metaAccountName}
                  onChange={(e) => setMetaAccountName(e.target.value)}
                  className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-t1blue focus:border-t1blue sm:text-sm"
                  placeholder="Enter account name"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Account Number
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-t1blue focus:border-t1blue sm:text-sm"
                    placeholder="Enter account number"
                  />
                  <button
                    type="button"
                    onClick={checkAffiliation}
                    disabled={!accountNumber.trim() || verificationStatus === VerificationStatus.CHECKING}
                    className={`px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                      verificationStatus === VerificationStatus.VERIFIED
                        ? 'bg-green-600 cursor-not-allowed'
                        : 'bg-t1blue hover:bg-t1blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-t1blue'
                    } disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150`}
                  >
                    {verificationStatus === VerificationStatus.CHECKING ? (
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : verificationStatus === VerificationStatus.VERIFIED ? (
                      <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      "Check"
                    )}
                  </button>
                </div>
                {verificationStatus !== VerificationStatus.IDLE && (
                  <div className={`mt-2 p-2 text-sm rounded-md ${
                    verificationStatus === VerificationStatus.VERIFIED ? 'bg-green-50 text-green-700 border border-green-200' : 
                    verificationStatus === VerificationStatus.FAILED ? 'bg-red-50 text-red-700 border border-red-200' : 
                    'bg-blue-50 text-blue-700 border border-blue-200'
                  }`}>
                    <div className="flex items-start">
                      {verificationStatus === VerificationStatus.VERIFIED && (
                        <svg className="h-5 w-5 text-green-400 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                      {verificationStatus === VerificationStatus.FAILED && (
                        <svg className="h-5 w-5 text-red-400 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      )}
                      {verificationStatus === VerificationStatus.CHECKING && (
                        <svg className="animate-spin h-5 w-5 text-blue-400 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      )}
                      <span>{verificationMessage}</span>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={metaPassword}
                    onChange={(e) => setMetaPassword(e.target.value)}
                    className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-t1blue focus:border-t1blue sm:text-sm pr-10"
                    placeholder="Enter password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                        <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                        <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Only show server selection after affiliation check passes */}
              {verificationStatus === VerificationStatus.VERIFIED && (
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    XM Global Server
                  </label>
                  {isLoadingServers ? (
                    <div className="flex items-center space-x-2 py-2">
                      <svg className="animate-spin h-5 w-5 text-t1blue" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-sm text-gray-600">Loading servers...</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex space-x-2">
                        <select
                          id="meta-server"
                          name="meta-server"
                          className={`block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-t1blue focus:border-t1blue sm:text-sm ${serverOptions.length === 0 ? 'bg-orange-50' : ''}`}
                          value={selectedServer}
                          onChange={(e) => setSelectedServer(e.target.value)}
                        >
                          <option value="">Select a server</option>
                          {serverOptions.map((server) => (
                            <option key={server.name} value={server.name}>{server.description}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => searchServers()}
                          title="Search for XM Global Servers"
                          className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-t1blue transition-colors duration-150"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </button>
                      </div>
                      
                      {serverOptions.length === 0 && (
                        <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-md">
                          <p className="text-sm text-orange-700">
                            No servers found. Please enter your server name manually below.
                          </p>
                        </div>
                      )}
                      
                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Manual Server Entry</h4>
                        <div className="flex space-x-2">
                          <input
                            type="text"
                            placeholder="e.g., XMGlobal-MT5 4"
                            className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-t1blue focus:border-t1blue sm:text-sm"
                            value={customServerName}
                            onChange={(e) => setCustomServerName(e.target.value)}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (customServerName.trim()) {
                                searchServers(customServerName.trim());
                              }
                            }}
                            disabled={!customServerName.trim()}
                            className="px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-t1blue hover:bg-t1blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-t1blue disabled:opacity-50 transition-colors duration-150"
                          >
                            Add
                          </button>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          Your server name is case-sensitive and should match exactly what's shown in your member portal.
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}

              {accountError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{accountError}</p>
                    </div>
                  </div>
                </div>
              )}

              {accountSuccess && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-green-700">{accountSuccess}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setIsAddingAccount(false);
                  setSelectedUserForAccount(null);
                  setAccountNumber('');
                  setMetaAccountName('');
                  setMetaPassword('');
                  setSelectedServer('');
                  setCustomServerName('');
                  setUseCustomServer(false);
                  setVerificationStatus(VerificationStatus.IDLE);
                  setVerificationMessage('');
                  setAccountError('');
                  setAccountSuccess('');
                }}
                className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-t1blue transition-colors duration-150"
              >
                Cancel
              </button>
              <button
                onClick={addTradingAccount}
                disabled={isSubmitting}
                className="px-4 py-2 bg-t1blue text-white text-sm font-medium rounded-md shadow-sm hover:bg-t1blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-t1blue transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Adding...' : 'Add Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Load More Button */}
        {users.length >= 50 && (
        <div className="flex justify-center py-4 border-t border-gray-200">
          <button
            onClick={loadMoreUsers}
            disabled={isLoadingMore}
            className="px-4 py-2 bg-t1blue text-white rounded-md hover:bg-blue-600 transition-colors duration-200 flex items-center"
          >
            {isLoadingMore ? (
              <>
                <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading more users...
              </>
            ) : (
              <>
                <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Load More Users
              </>
            )}
          </button>
        </div>
      )}
      </div>
    </div>
  );
};

export default AdminDashboard;